// ================================================================
//  EditBizEventVersionController
//  Opens the biz event config wizard in EDIT mode for a specific version.
//
//  Locals injected:
//    - bizEvent   : the parent event header (biz_event_id, biz_event_name, etc.)
//    - version    : the specific version object (has biz_event_sources, biz_event_target,
//                   biz_event_mappings, biz_event_trigger, etc.)
//    - profile    : current profile
// ================================================================

angular.module('bizmetryApp').controller('EditBizEventVersionController', function (
    $scope,
    $rootScope,
    $q,
    $mdDialog,
    $mdToast,
    $timeout,
    $interval,
    ApiCollectionService,
    TemplateInstanceService,
    BusinessEventsService,
    ProfileService,
    dialogService,
    BmelEditorService,
    bizEvent,
    version,
    profile
) {
    const ctrl = this;
    $scope.ctrl = ctrl;

    // ========================================
    // EDIT MODE FLAGS
    // ========================================

    ctrl.isEditMode      = true;
    ctrl.bizEvent        = bizEvent;         // parent event header (read-only fields)
    ctrl.versionData     = version;          // original version snapshot
    ctrl.hasChanges      = false;            // drives "Apply Changes" enabled state
    ctrl.applying        = false;            // spinner while PUT is in flight
    ctrl.loadingVersion  = true;             // while we fetch the full version detail

    // ========================================
    // WIZARD STATE — 4 steps (General is read-only, no Step 1 identity form)
    // Steps: 1-General(RO)  2-Target Frame  3-Select Operations  4-Configure Operations  5-Configure Mapping
    // We keep the same numbering as create but Step 1 is fully read-only.
    // ========================================

    // Edit mode: 4 steps only — identity & trigger type are read-only and shown
    // in the toolbar subtitle, not as navigable wizard steps.
    ctrl.steps = [
        { label: 'Target Frame' },
        { label: 'Select Operations' },
        { label: 'Configure Operations' },
        { label: 'Configure Mapping' }
    ];

    // Symbolic step constants (1-based) for readability
    ctrl.STEP_TARGET  = 1;
    ctrl.STEP_OPS     = 2;
    ctrl.STEP_CONFIG  = 3;
    ctrl.STEP_MAPPING = 4;

    ctrl.totalSteps  = ctrl.steps.length;
    ctrl.currentStep = 1;

    ctrl.stepLabel = function () { return ctrl.steps[ctrl.currentStep - 1].label; };

    // ========================================
    // STEP 1 — EVENT IDENTITY (read-only)
    // ========================================

    ctrl.bizEventName        = bizEvent.biz_event_name        || '';
    ctrl.bizEventDescription = bizEvent.biz_event_description || '';
    // Trigger type is also read-only
    ctrl.triggerType = (version.biz_event_trigger && version.biz_event_trigger.biz_event_trigger_id) || 'REST-API';

    // ========================================
    // STEP 2 — SOURCE TYPE (read-only — derived from trigger)
    // ========================================

    // Grammar is locked to the trigger (read-only in edit mode) — only its
    // badge is shown, so no catalog lookup needed
    ctrl.selectedSourceType = BusinessEventsService.describeGrammar(ctrl.triggerType);

    // ========================================
    // STEP 3 — SELECT TARGET FRAME (editable)
    // ========================================

    ctrl.publishedTemplates    = [];
    ctrl.loadingTemplates      = false;
    ctrl.selectedTemplate      = null;
    ctrl.selectedTemplateFull  = null;
    ctrl.frameTypes            = [];
    ctrl.loadingFrameTypes     = false;
    ctrl.selectedFrameType     = null;

    // ── Original state snapshots — used by _recomputeDirty() ──────────────
    ctrl._originalTarget = version.biz_event_target
        ? angular.copy(version.biz_event_target)
        : null;

    // Canonical, content-based key for a single filter — used for both the
    // original (API shape) and current (frontend shape) filters so a filter
    // that's been edited and then edited back matches its original again.
    // allowedValues is sorted since it's a set, not an ordered list.
    function _origFilterKey(f) {
        var vals = (f.allowed_values || []).slice().sort().join(',');
        return (f.filter_type || '') + '|' + (f.source_path || '') + '|' + (f.case_sensitive !== false) + '|' + vals;
    }
    function _curFilterKey(f) {
        var vals = (f.allowedValues || []).slice().sort().join(',');
        return (f.filterType || '') + '|' + (f.sourcePath || '') + '|' + (f.caseSensitive !== false) + '|' + vals;
    }

    // Canonical key for each original source operation (order-independent)
    ctrl._originalSourceKeys = (version.biz_event_sources || []).map(function (src) {
        return (src.api_collection_id || '') + '|' +
               (src.operation_id      || src.operation_name || '') + '|' +
               (src.event_interception_mode || '') + '|' +
               (src.cardinality || 'ONE') + '|' +
               (src.correlation_key ? src.correlation_key.source_type + ':' + src.correlation_key.source_path : '');
    }).sort().join(';;');

    // Original per-operation config, keyed by collection+operation identity (no
    // config in the key this time) — lets _addOperation restore an operation's
    // exact original interceptionMode/correlationKey/filters when it's re-added
    // after being removed. Without this, removing then re-adding the very same
    // operation resets it to blank defaults (INBOUND, no correlation, no
    // filters) instead of what it actually had, so _recomputeDirty() correctly
    // — but confusingly, from the user's point of view — keeps reporting a
    // change even though "the same operation is selected again".
    ctrl._originalSourcesByKey = {};
    (version.biz_event_sources || []).forEach(function (src) {
        var key = (src.api_collection_id || '') + '|' + (src.operation_id || src.operation_name || '');
        ctrl._originalSourcesByKey[key] = src;
    });

    // Canonical, content-based key for every operation's filter set (order-
    // independent within each operation, and across operations) — replaces a
    // filter-count-only proxy that couldn't tell "a filter's value changed"
    // from "nothing changed", so any filter edit permanently latched
    // hasChanges = true even if you edited it right back to what it was.
    ctrl._originalFiltersKey = (version.biz_event_sources || []).map(function (src) {
        var opId = (src.api_collection_id || '') + '|' + (src.operation_id || src.operation_name || '');
        var filterKeys = (src.filters || []).map(_origFilterKey).sort().join(',');
        return opId + '::' + filterKeys;
    }).sort().join(';;');

    // Original mapping count — used as a quick proxy for mapping changes
    ctrl._originalMappingCount = (version.biz_event_mappings || []).length;

    ctrl.shortId = function (id, n) { return id ? id.slice(-(n || 10)) : ''; };

    ctrl.loadPublishedTemplates = function () {
        ctrl.loadingTemplates = true;
        ctrl.publishedTemplates = [];

        // Only template versions with API collections: a biz event can use no
        // other APIs than its template's.
        ProfileService.getTemplateTree(profile.id, null, { withApiCollections: true })
            .then(function (response) {
                var data = response && response.data ? response.data : null;
                var root = data;
                if (root && root['template-tree']) { root = root['template-tree']; }
                if (root && root.templateTree)      { root = root.templateTree; }
                if (!root || !root.id) { ctrl.publishedTemplates = []; return; }

                var flat = [];
                var byId = {};   // every version in the tree, selectable or not
                function walk(node) {
                    if (!node || !node.id) { return; }
                    var status  = node.status  || node.templateStatus;
                    var version = node.version || node.templateVersion;
                    var entry = {
                        id:      node.id,
                        version: version,
                        status:  status,
                        name:    node.name || node.templateName || node.templateDesc || null
                    };
                    byId[node.id] = entry;
                    if (_isSelectableStatus(status) && !node.filtered_out) { flat.push(entry); }
                    (node.children || []).forEach(function (child) { walk(child); });
                }
                walk(root);
                // Compute which templateInstanceIds are already in use by OTHER
                // versions of this biz event (excluding the version being edited).
                // These entries will be greyed out and unclickable in the selector.
                var currentVersionId = ctrl.versionData && ctrl.versionData.biz_event_version_id;
                var currentTplId     = ctrl._originalTarget && ctrl._originalTarget.template_instance_id;
                var usedTemplateIds  = {};
                ((bizEvent && bizEvent.versions) || []).forEach(function (v) {
                    if (v.biz_event_version_id === currentVersionId) { return; }
                    var tplId = v.biz_event_target && v.biz_event_target.template_instance_id;
                    if (tplId && tplId !== currentTplId) { usedTemplateIds[tplId] = true; }
                });

                // Exclude template versions already assigned to other versions
                // of this biz event — they cannot be selected for this version.
                ctrl.publishedTemplates = flat.filter(function (t) {
                    return !usedTemplateIds[t.id];
                });

                // The template version this biz event version is built on is always listed
                // and selected — even when the tree filters it out (retired, no API
                // collections left) or another version of the event shares it.
                if (currentTplId) {
                    var current = ctrl.publishedTemplates.find(function (t) { return t.id === currentTplId; });
                    if (!current) {
                        current = byId[currentTplId] || {
                            id:      currentTplId,
                            version: ctrl._originalTarget.template_instance_version,
                            status:  null,
                            name:    null
                        };
                        ctrl.publishedTemplates.unshift(current);
                    }
                    ctrl.selectTemplate(current, true);   // also selects the version's frame type
                }
            })
            .catch(function (err) {
                console.error('[EditBizEventVersion] Error loading templates:', err);
            })
            .finally(function () { ctrl.loadingTemplates = false; });
    };

    ctrl.selectTemplate = function (node, suppressDirty) {
        if (ctrl.selectedTemplate && ctrl.selectedTemplate.id === node.id) { return; }
        // The preload selects the version's own template; anything after that is the user's.
        ctrl._templateChangedByUser = !suppressDirty;
        ctrl.selectedTemplate     = node;
        ctrl.selectedTemplateFull = null;
        ctrl.selectedFrameType    = null;
        ctrl.frameTypes           = [];
        ctrl.loadingFrameTypes    = true;

        TemplateInstanceService.getTemplateInstanceById(node.id)
            .then(function (response) {
                var full = response.data || response;
                ctrl.selectedTemplateFull = full;
                ctrl.frameTypes = (full.templateConfig && full.templateConfig.frame_types) || [];

                // Auto-select the current frame type
                if (ctrl._originalTarget && ctrl._originalTarget.frame_type_id) {
                    var matchFt = ctrl.frameTypes.find(function (ft) {
                        return (ft.frame_type_id || ft.id) === ctrl._originalTarget.frame_type_id;
                    });
                    if (matchFt) {
                        ctrl.selectedFrameType = matchFt;
                        // The version's own template (preload): the components its saved
                        // mappings were built for — compared against the frame the user
                        // ends up on (see _currentAgentInputs).
                        if (suppressDirty) {
                            ctrl._originalFrameComponents = _componentsSignature(matchFt);
                            ctrl._originalResourceSchema = _resourceSchema();
                        }
                    }
                }

                if (!suppressDirty) { ctrl._recomputeDirty(); }
            })
            .catch(function (err) {
                console.error('[EditBizEventVersion] Error loading template:', err);
                ctrl.frameTypes = [];
            })
            .finally(function () { ctrl.loadingFrameTypes = false; });
    };

    // Version of the template the frame types come from — shown as a pill next to the
    // frame type everywhere in the wizard (list, IntelliSense bar/results, composer).
    ctrl.templateVersion = function () {
        var t = ctrl.selectedTemplate, f = ctrl.selectedTemplateFull;
        return (t && (t.version || t.templateVersion)) || (f && (f.templateVersion || f.version)) || null;
    };

    ctrl.selectFrameType = function (ft) {
        ctrl.selectedFrameType = ft;
        ctrl._recomputeDirty();
    };

    ctrl.frameTypeName = function (ft) {
        return ft.frame_name || ft.name || ft.frame_type_name || '(unnamed)';
    };
    ctrl.frameTypeDesc = function (ft) {
        return ft.frame_description || ft.description || ft.frame_type_description || '';
    };
    ctrl.frameTypeComponentCount = function (ft) {
        return (ft.frame_fields || ft.components || ft.frame_components || []).length;
    };

    // ========================================
    // STEP 4 — SELECT OPERATIONS (editable)
    // ========================================

    ctrl.loadingCollections     = false;
    ctrl.browsingCollection     = null;
    ctrl.scopedCollections      = [];
    ctrl.operations             = [];
    ctrl.displayedOperations    = [];
    ctrl.loadingOperations      = false;
    ctrl.operationSearch        = '';
    ctrl.selectedOperations     = [];
    ctrl.intelliSenseSelecting  = false;
    ctrl.intelliSenseSelectionError = null;
    ctrl._intelliSenseMappings  = null;
    ctrl._pendingScopeCollectionId = null;
    ctrl.collectionSearch       = '';

    // The API collections the selected template version exposes (template
    // editor → API Collections), one version per collection. Biz events consume
    // APIs through their template, so these are the only ones offered.
    ctrl.templateApiCollections     = [];
    ctrl.loadingTemplateCollections = false;

    $scope.$watch(function () { return ctrl.selectedTemplate && ctrl.selectedTemplate.id; }, function (templateId) {
        ctrl.templateApiCollections = [];
        if (!templateId) { return; }
        ctrl.loadingTemplateCollections = true;
        TemplateInstanceService.getApiCollections(templateId, { skipLoader: true })
            .then(function (res) {
                if (!ctrl.selectedTemplate || ctrl.selectedTemplate.id !== templateId) { return; }
                ctrl.templateApiCollections = (res.data && res.data.api_collections) || [];
                if (ctrl._templateChangedByUser) { _alignSelectionToTemplate(ctrl.templateApiCollections); }
            })
            .catch(function () { ctrl.templateApiCollections = []; })
            .finally(function () { ctrl.loadingTemplateCollections = false; });
    });

    // A biz event only uses the collection versions linked to its template. When
    // the user moves the version to another template, each selected operation of a
    // collection that template doesn't link moves to the linked version of the same
    // collection (same operation, kept at its index so its mappings survive) or,
    // when there's no such operation, is dropped along with its mappings. "Same
    // operation": same id, else same method + path, else the one the contract diff
    // says it moved to (OPERATION_MOVED: same name, new method / path).
    function _alignSelectionToTemplate(linked) {
        var linkedIds = {}, byName = {};
        linked.forEach(function (c) { linkedIds[c.collection_id] = true; byName[c.collection_name] = c; });

        ctrl.scopedCollections = ctrl.scopedCollections.filter(function (s) {
            return linkedIds[s.collection.collection_id];
        });
        if (ctrl.browsingCollection && !linkedIds[ctrl.browsingCollection.collection_id]) {
            ctrl.browsingCollection  = null;
            ctrl.operations          = [];
            ctrl.displayedOperations = [];
        }

        var stale = ctrl.selectedOperations.filter(function (s) { return !linkedIds[s.collection.collection_id]; });
        if (!stale.length) { return; }

        var targetIds = {}, pairs = {};
        stale.forEach(function (s) {
            var t = byName[s.collection.collection_name];
            if (!t) { return; }
            targetIds[t.collection_id] = true;
            pairs[s.collection.collection_id + '>' + t.collection_id] = { fromId: s.collection.collection_id, toId: t.collection_id };
        });
        // The contract diff of each (from → to) pair: it tells moved operations apart,
        // and later prunes the mappings reading fields the new version lost.
        var pairList = Object.keys(pairs).map(function (k) { return pairs[k]; });
        var changesPromise = $q.all(pairList.map(function (p) {
            return ApiCollectionService.getChanges(p.toId, p.fromId)
                .then(function (res) { return { pair: p, changes: (res && res.changes) || [] }; })
                .catch(function () { return { pair: p, changes: [] }; });
        }));
        $q.all([$q.all(Object.keys(targetIds).map(function (id) {
            return ApiCollectionService.getById(id)
                .then(function (r) { return r && r.data ? r.data : r; })
                .catch(function () { return null; });
        })), changesPromise]).then(function (results) {
            var fulls = results[0], diffs = results[1];
            // from collection | previous operation id → id of the operation it moved to
            var movedTo = {};
            diffs.forEach(function (d) {
                d.changes.forEach(function (c) {
                    if (c.changeType === 'OPERATION_MOVED' && c.operationId && c.newOperationId) {
                        movedTo[d.pair.fromId + '|' + c.operationId] = c.newOperationId;
                    }
                });
            });
            var opsByCollection = {};
            fulls.forEach(function (full) {
                if (!full) { return; }
                opsByCollection[full.collection_id] = {
                    collection: full,
                    ops: full.operations ? ctrl._opsFromMetadata(full.operations) : ctrl._parseOperations(full.swagger)
                };
            });

            var moved = [], dropped = [], relocated = [], toRemove = [], movedPairs = {}, idMoves = {};
            stale.forEach(function (entry) {
                var t = byName[entry.collection.collection_name];
                var target = t && opsByCollection[t.collection_id];
                var movedId = movedTo[entry.collection.collection_id + '|' + entry.op.operationId];
                var match = target && (target.ops.find(function (op) {
                    return op.operationId && op.operationId === entry.op.operationId;
                }) || target.ops.find(function (op) {
                    return op._method === entry.op._method && op._pathKey === entry.op._pathKey;
                }) || (movedId && target.ops.find(function (op) {
                    return op.operationId === movedId;
                })));
                if (match && entry.op.operationId && match.operationId && match.operationId !== entry.op.operationId) {
                    idMoves[entry.op.operationId] = match.operationId;
                    relocated.push(_opLabel(entry.op) + ' → ' + match.method + ' ' + match.path);
                }
                if (!match) {
                    dropped.push(_opLabel(entry.op));
                    toRemove.push(entry);
                    return;
                }
                // Already selected from the new version too: keep this entry (it holds the mappings).
                ctrl.selectedOperations.forEach(function (other) {
                    if (other !== entry && other.collection.collection_id === target.collection.collection_id &&
                        other.op._method === match._method && other.op._pathKey === match._pathKey) {
                        toRemove.push(other);
                    }
                });
                var optionsByType = {};
                ctrl.correlationSourceTypes.forEach(function (cst) {
                    var opts = _computeCorrelationOptions(match, cst.id);
                    opts.sort(function (a, b) { return a.name < b.name ? -1 : a.name > b.name ? 1 : 0; });
                    optionsByType[cst.id] = opts;
                });
                movedPairs[entry.collection.collection_id + '>' + target.collection.collection_id] = {
                    fromId: entry.collection.collection_id, toId: target.collection.collection_id
                };
                entry.op = match;
                entry.collection = target.collection;
                entry._optionsByType = optionsByType;
                moved.push(target.collection);
            });

            toRemove
                .map(function (e) { return { entry: e, idx: ctrl.selectedOperations.indexOf(e) }; })
                .filter(function (r) { return r.idx !== -1; })
                .sort(function (a, b) { return b.idx - a.idx; })
                .forEach(function (r) { _doRemoveSelectedOperation(r.entry, r.idx); });

            ctrl._intelliSenseMappings = null;
            // An operation that now has another id (moved in the new contract) keeps its
            // mappings: the loaded ones cite it by its previous id.
            _renameOperationIds(ctrl._preloadMappings, idMoves);
            ctrl._recomputeDirty();

            // The moved operations now follow another contract: before the composer
            // opens, mappings reading fields it no longer has are dropped and
            // IntelliSense maps what's left empty (see _remapMovedOperations). The diff
            // reports field changes under the previous operation id: they're re-keyed.
            if (Object.keys(movedPairs).length) {
                ctrl._remapPending = true;
                diffs.forEach(function (d) {
                    if (!movedPairs[d.pair.fromId + '>' + d.pair.toId]) { return; }
                    ctrl._repointChanges = ctrl._repointChanges.concat(d.changes.map(function (c) {
                        return idMoves[c.operationId] ? angular.extend({}, c, { operationId: idMoves[c.operationId] }) : c;
                    }));
                });
                ctrl._repointChangesPromise = $q.when();
            }

            var lines = [];
            if (moved.length) {
                var versions = {};
                moved.forEach(function (c) { versions[c.collection_name + ' v' + c.api_version] = true; });
                lines.push('Operations moved to ' + Object.keys(versions).join(', '));
            }
            if (relocated.length) {
                lines.push('Moved in the new contract: ' + relocated.join(', '));
            }
            if (dropped.length) {
                lines.push('Removed (not in this template\'s API collections): ' + dropped.join(', '));
            }
            if (lines.length) {
                $mdToast.show($mdToast.simple().textContent(lines.join('. ')).position('top right').hideDelay(6000));
            }
        });
    }

    ctrl._remapPending   = false;
    // Operations were (re)picked with IntelliSense: the agent builds their
    // mappings once, when leaving Configure Operations (see ctrl.nextStep).
    ctrl._agentPending   = false;
    // Source/target the agent last mapped in this session ({frameTypeId,
    // operations: {opId: cardinality}}): leaving Configure Operations with a
    // different one runs it again for what changed (see _agentRemapNeeded).
    ctrl._agentInputs    = null;
    // Source/target the saved mappings were built for (the version as loaded): a
    // pending IntelliSense selection that ends up on that same configuration — e.g.
    // an operation removed and added back — doesn't need the agent again.
    ctrl._mappedInputs   = null;
    // Component signature of the version's original frame ({componentId: type}), set
    // when its template loads — _mappedInputs is built before that, so it resolves lazily.
    ctrl._originalFrameComponents = null;
    ctrl._repointChanges = [];

    // ── Mappings of operations moved to another collection version ──────────
    // Same rules as the server-side recompute (BizEventMappingReconciler): a
    // mapping source is dropped when its field was removed, or changed type, in
    // the new contract — for params the "$." prefix is ignored, headers compare
    // case-insensitively, and a change also hits the fields below it.
    function _normPath(path, param) {
        if (!path) { return null; }
        var p = String(path).trim().replace(/\[(\d*|\*)\]/g, '').replace(/\.\d+(?=\.|$)/g, '');
        while (/\.CONTENT$/.test(p)) { p = p.slice(0, -'.CONTENT'.length); }
        if (param && p.indexOf('$.') === 0) { p = p.slice(2); }
        return p;
    }

    function _changeHits(c, operationId, sourceType, path) {
        if (String(c.operationId) !== String(operationId)) { return false; }
        if (c.changeType !== 'FIELD_REMOVED' && c.changeType !== 'FIELD_TYPE_CHANGED') { return false; }
        if (!c.fieldPath || !sourceType || c.sourceType !== sourceType) { return false; }
        var param = sourceType !== 'REQUEST_FIELD' && sourceType !== 'RESPONSE_FIELD';
        var changed = _normPath(c.fieldPath, param), used = _normPath(path, param);
        if (!changed || !used) { return false; }
        if (sourceType === 'REQUEST_HEADER') { return used.toLowerCase() === changed.toLowerCase(); }
        return used === changed || used.indexOf(changed + '.') === 0;
    }

    /** @return {{ mappings: Array, dropped: string[] }} base mappings (IntelliSense shape) without the broken sources */
    function _dropBrokenMappings(base, changes) {
        var dropped = [];
        if (!changes.length) { return { mappings: base, dropped: dropped }; }
        function hit(opId, type, path) {
            return changes.some(function (c) { return _changeHits(c, opId, type, path); });
        }
        var kept = [];
        angular.copy(base || []).forEach(function (comp) {
            var label = comp.frame_field_name || comp.frame_component_id;
            comp.sources = (comp.sources || []).filter(function (src) {
                src.column_mappings = (src.column_mappings || []).filter(function (cm) {
                    if (hit(src.operation_id, src.source_type, cm.field_path)) {
                        dropped.push(label + '.' + cm.column_name);
                        return false;
                    }
                    return true;
                });
                if (hit(src.operation_id, src.source_type, src.field_path)) {
                    dropped.push(label);
                    return false;
                }
                return true;
            });
            if (comp.sources.length) { kept.push(comp); }
        });
        return { mappings: kept, dropped: dropped };
    }

    // What the frame's mappings depend on: each component (id + type, nested-table
    // columns included). A new template version keeps the frame_type_id — e.g.
    // agent-invocation v1.1 → v1.2 with 5 new inference-parameter components — so the
    // id alone never told the frame had changed and IntelliSense didn't run again.
    function _componentsSignature(ft) {
        if (!ft) { return null; }
        var sig = {};
        (ft.frame_fields || ft.components || ft.frame_components || []).forEach(function (c) {
            var id = c.frame_component_id || c.frame_field_id || c.component_id || c.id;
            if (!id) { return; }
            var cols = (c.nested_table_columns || []).map(function (col) {
                return col.column_name || col.name || col.column_id || col.id;
            }).sort();
            sig[String(id)] = (c.frame_component_type || c.component_type || '') +
                              (cols.length ? '[' + cols.join(',') + ']' : '');
        });
        return sig;
    }

    // Components an inputs snapshot was built for; the version-as-loaded snapshot
    // (frameComponents undefined) takes the original frame's once its template loaded.
    // ASBOs used only inside BMEL are dependencies too, even for a CUSTOM target.
    function _resourceSchema() {
        var cfg = (ctrl.selectedTemplateFull || {}).templateConfig || {};
        var result = {};
        (cfg.resource_types || []).forEach(function (r) {
            result[String(r.name || '').toLowerCase()] = JSON.stringify([r.id,
                (cfg.attribute_types || []).filter(function (a) {
                    return (a.resource_id || a.resource_type_id) === r.id;
                }).map(function (a) {
                    return [a.id, a.name, a.column_metadata_name, a.scalar_attribute_type,
                        !!a.isPrimaryKey, !!a.isResourceNameField];
                }).sort(function (a, b) { return String(a[0]).localeCompare(String(b[0])); })]);
        });
        return result;
    }
    function _schemaOf(inputs) {
        return inputs.resourceSchema === undefined ? ctrl._originalResourceSchema : inputs.resourceSchema;
    }
    function _resourceSchemaChanged(a, b) {
        a = _schemaOf(a); b = _schemaOf(b);
        if (!a || !b) { return false; }
        return Object.keys(a).some(function (name) { return a[name] !== b[name]; }) ||
            Object.keys(b).some(function (name) { return a[name] !== b[name]; });
    }
    function _hasInvalidResourceReference(comp) {
        if (!ctrl.selectedTemplateFull) { return false; }
        var resources = BmelEditorService.buildResourceTypes(ctrl.selectedTemplateFull);
        function invalid(value) {
            if (typeof value === 'string') {
                return value.indexOf('#{') >= 0 && BmelEditorService.resourceReferenceErrors(value, resources).length > 0;
            }
            return value && typeof value === 'object' && Object.keys(value).some(function (key) { return invalid(value[key]); });
        }
        if (invalid(comp)) { return true; }
        return Object.keys(ctrl.targetExpressions || {}).some(function (key) {
            return key.split('|col|')[0] === String(comp.frame_component_id) && invalid(ctrl.targetExpressions[key]);
        });
    }

    function _hasStaleResourceReference(comp, previous, current) {
        var before = _schemaOf(previous), after = _schemaOf(current);
        if (!before || !after) { return false; }
        var expressions = [JSON.stringify(comp)];
        Object.keys(ctrl.targetExpressions || {}).forEach(function (key) {
            if (key.split('|col|')[0] === String(comp.frame_component_id)) {
                expressions.push(ctrl.targetExpressions[key]);
            }
        });
        return expressions.some(function (expression) {
            var match, refs = /bmel:get-resource-by-id\s*\(\s*#\{([^}]+)}/g;
            while ((match = refs.exec(String(expression)))) {
                var name = match[1].trim().toLowerCase();
                if (before[name] !== after[name]) { return true; }
            }
            return false;
        });
    }

    function _componentsOf(inputs) {
        return inputs.frameComponents === undefined ? ctrl._originalFrameComponents : inputs.frameComponents;
    }

    // Same source/target: frame, operations + cardinality and — when both are known —
    // the frame's components.
    function _sameInputs(a, b) {
        if (_inputsKey(a) !== _inputsKey(b) || _resourceSchemaChanged(a, b)) { return false; }
        var ca = _componentsOf(a), cb = _componentsOf(b);
        if (!ca || !cb) { return true; }
        var ka = Object.keys(ca).sort(), kb = Object.keys(cb).sort();
        return ka.length === kb.length && ka.every(function (k, i) { return k === kb[i] && ca[k] === cb[k]; });
    }

    // What the agent's mappings depend on: the target frame (and its components) and
    // each selected operation with its cardinality. Pass frameComponents = undefined
    // for the version as loaded (resolved lazily, see _componentsOf).
    function _currentAgentInputs(frameTypeId, frameComponents) {
        var operations = {};
        var aliases    = {};   // every id a mapping may cite for the operation → its key here
        ctrl.selectedOperations.forEach(function (s) {
            var key = String(s.op.operationId || s.op.operation_id || s.op.id);
            operations[key] = s.cardinality || 'ONE';
            [s.op.operationId, s.op.operation_id, s.op.id, s.op.operationName, s.op.operation_name]
                .forEach(function (a) { if (a) { aliases[String(a)] = key; } });
        });
        return {
            frameTypeId:     frameTypeId || (ctrl.selectedFrameType ? ctrl.selectedFrameType.frame_type_id : null),
            frameComponents: arguments.length > 1 ? frameComponents : _componentsSignature(ctrl.selectedFrameType),
            resourceSchema:  arguments.length > 1 ? undefined : _resourceSchema(),
            operations:      operations,
            aliases:         aliases
        };
    }

    // IntelliSense picked the operations (Proceed in its results dialog) and they
    // differ from what the current mappings were built for — the last agent run in
    // this session or, before any, the version as loaded.
    function _agentSelectionNeedsMapping() {
        if (!ctrl._agentPending) { return false; }
        var baseline = ctrl._agentInputs || ctrl._mappedInputs;
        return !baseline || !_sameInputs(baseline, _currentAgentInputs());
    }

    function _inputsKey(inputs) {
        return JSON.stringify([inputs.frameTypeId, Object.keys(inputs.operations).sort().map(function (id) {
            return id + ':' + inputs.operations[id];
        })]);
    }

    // The source/target changed since the current mappings were built — the last
    // agent run in this session or, before any, the version as loaded: operations,
    // collections, cardinality (even alone), the target frame or its components
    // (a template version that added / removed / retyped components of the frame).
    function _agentRemapNeeded() {
        var baseline = ctrl._agentInputs || ctrl._mappedInputs;
        return !!baseline && ctrl.selectedOperations.length > 0
            && (!_sameInputs(baseline, _currentAgentInputs()) || (!ctrl._agentInputs &&
                (ctrl._preloadMappings || []).some(_hasInvalidResourceReference)));
    }

    // Mappings the agent must redo: everything when the target frame changed;
    // otherwise the components fed by an operation that was removed or whose
    // cardinality changed (their refs / aggregations no longer fit), and the
    // components the new template version removed or retyped. Components it added
    // have no mapping yet: the agent fills them as gaps.
    // The dropped components also lose their BMEL expressions: they live apart, in
    // ctrl.targetExpressions, and the composer never overwrites an existing one — so
    // the agent's new expressions would otherwise be hidden behind the stale ones
    // (e.g. MANY → ONE → MANY kept the ONE refs).
    function _dropStaleMappings(base, previous, current) {
        if (!previous) { return base; }
        var kept;
        if (previous.frameTypeId !== current.frameTypeId) {
            kept = [];
        } else {
            var stale = {};
            Object.keys(previous.operations).forEach(function (id) {
                if (current.operations[id] !== previous.operations[id]) { stale[id] = true; }
            });
            var aliases = previous.aliases || {};
            var prevComps = _componentsOf(previous), curComps = _componentsOf(current);
            kept = base.filter(function (comp) {
                if (_hasInvalidResourceReference(comp) || _hasStaleResourceReference(comp, previous, current)) { return false; }
                var cid = String(comp.frame_component_id);
                if (prevComps && curComps && prevComps[cid] !== undefined && prevComps[cid] !== curComps[cid]) {
                    return false;   // removed or retyped by the new template version
                }
                return !(comp.sources || []).some(function (src) {
                    var id = String(src.operation_id);
                    return stale[aliases[id] || id];
                });
            });
        }
        var keptIds = {};
        kept.forEach(function (c) { keptIds[c.frame_component_id] = true; });
        _forgetExpressions(base.filter(function (c) { return !keptIds[c.frame_component_id]; })
            .map(function (c) { return c.frame_component_id; }));
        return kept;
    }

    // Removes the BMEL expressions of these components (and of their columns).
    function _forgetExpressions(compIds) {
        if (!compIds.length || !ctrl.targetExpressions) { return; }
        var drop = {};
        compIds.forEach(function (id) { drop[id] = true; });
        Object.keys(ctrl.targetExpressions).forEach(function (key) {
            if (drop[key.split('|col|')[0]]) { delete ctrl.targetExpressions[key]; }
        });
    }

    // Agent pass for operations picked with IntelliSense (or changed since the
    // agent last mapped them): maps them with their cardinality and merges into
    // what's already mapped — it fills gaps and redoes the stale components.
    // On cancel / failure the composer opens with the existing mappings, and we
    // don't retry for this configuration on every Next.
    function _runAgentMapping() {
        var inputs = _currentAgentInputs();
        var base = (ctrl.mappings && ctrl.mappings.length)
            ? _convertApiMappingsToIntelliSenseFormat(_buildMappingsPayload())
            : (ctrl._preloadMappings || []);
        base = _dropStaleMappings(base, ctrl._agentInputs || ctrl._mappedInputs, inputs);
        var ops = ctrl.selectedOperations.map(function (s) { return _toApiOperation(s.op, s.cardinality); });
        return _runIntelliSenseJob(ops, {
            engine:       BusinessEventsService.INTELLISENSE_ENGINE.AGENT,
            dialogTitle:  'Building Mappings...',
            initialStage: 'The IntelliSense agent is mapping your operations…'
        })
            .then(function (res) {
                ctrl._agentInputs = inputs;
                return _confirmReplaceMapped(res.mappings, base).then(function (replace) {
                    _handIntelliSenseToComposer(res.mappings, base, replace);
                });
            })
            .catch(function (err) {
                ctrl._agentInputs = inputs;
                if (err && !err.cancelled) {
                    dialogService.showErrorDialog(err.title || 'IntelliSense Failed',
                        (err.message || 'IntelliSense processing failed.') + ' You can run IntelliSense again from the mapping step.');
                }
            });
    }

    // The agent also proposes mappings for components this version already maps.
    // Ask whether its proposal overrides them (Yes: same result as creating the biz
    // event from scratch) or the existing ones stay and it only fills the gaps (No).
    function _confirmReplaceMapped(suggested, base) {
        var mapped = {};
        (base || []).forEach(function (c) {
            if ((c.sources || []).length) { mapped[c.frame_component_id] = c.frame_field_name || c.frame_component_id; }
        });
        var overlap = (suggested || []).filter(function (c) {
            return mapped[c.frame_component_id] && (c.sources || []).length;
        }).map(function (c) { return c.frame_field_name || mapped[c.frame_component_id]; });
        if (!overlap.length) { return $q.when(false); }
        return dialogService.showDecisionDialog(
            'Override existing mappings?',
            'IntelliSense mapped <strong>' + overlap.length + '</strong> component' +
            (overlap.length === 1 ? '' : 's') + ' that already ' + (overlap.length === 1 ? 'has' : 'have') +
            ' a mapping (' + overlap.slice(0, 6).join(', ') + (overlap.length > 6 ? ', …' : '') + ').<br><br>' +
            '<strong>Yes</strong> overrides them with IntelliSense\'s mapping and BMEL expression.<br>' +
            '<strong>No</strong> keeps the existing configuration and only maps the components that have none.',
            { goBack: 'No', proceed: 'Yes' }
        ).then(function (decision) { return decision === 'proceed'; });
    }

    // Forced IntelliSense pass, run when leaving Configure Operations after the
    // template change moved operations to another collection version.
    function _remapMovedOperations() {
        return $q.when(ctrl._repointChangesPromise).then(_remapWithChanges);
    }

    function _remapWithChanges() {
        var base = (ctrl.mappings && ctrl.mappings.length)
            ? _convertApiMappingsToIntelliSenseFormat(_buildMappingsPayload())
            : (ctrl._preloadMappings || []);
        var inputs = _currentAgentInputs();
        var pruned = _dropBrokenMappings(_dropStaleMappings(base, ctrl._agentInputs || ctrl._mappedInputs, inputs), ctrl._repointChanges || []);
        ctrl._repointChanges = [];

        var ops = ctrl.selectedOperations.map(function (s) { return _toApiOperation(s.op, s.cardinality); });
        return _runIntelliSenseJob(ops, {
            engine:       BusinessEventsService.INTELLISENSE_ENGINE.AGENT,
            dialogTitle:  'Updating Mappings...',
            initialStage: 'Mapping the new API collection version…'
        })
            .then(function (res) {
                _handIntelliSenseToComposer(res.mappings, pruned.mappings);
                ctrl._agentInputs = inputs;
                if (pruned.dropped.length) {
                    $mdToast.show($mdToast.simple()
                        .textContent('Remapped with IntelliSense. Dropped (field no longer in the contract): ' + pruned.dropped.join(', '))
                        .position('top right').hideDelay(6000));
                }
            })
            .catch(function (err) {
                // Keep the cleaned-up mappings even without IntelliSense.
                ctrl._preloadMappings = pruned.mappings;
                ctrl.mappings = [];
                ctrl._agentInputs = inputs;
                if (err && !err.cancelled) {
                    dialogService.showErrorDialog(err.title || 'IntelliSense Failed',
                        (err.message || 'IntelliSense processing failed.') + ' Map the remaining fields manually.');
                }
            });
    }

    // Rewrites the operation ids IntelliSense-shaped mappings cite (previous id → new id).
    function _renameOperationIds(mappings, idMoves) {
        if (!mappings || !Object.keys(idMoves).length) { return; }
        mappings.forEach(function (comp) {
            (comp.sources || []).forEach(function (src) {
                var to = idMoves[String(src.operation_id)];
                if (to) { src.operation_id = to; }
            });
        });
    }

    function _opLabel(op) {
        return (op && (op.operationName || op.summary || op.path)) || 'operation';
    }

    // Matches name, description or version.
    ctrl.filteredCollections = function () {
        var term = (ctrl.collectionSearch || '').trim().toLowerCase();
        if (!term) { return ctrl.templateApiCollections; }
        return ctrl.templateApiCollections.filter(function (c) {
            return (c.collection_name || '').toLowerCase().indexOf(term) !== -1 ||
                   (c.description || '').toLowerCase().indexOf(term) !== -1 ||
                   (c.api_version || '').toLowerCase().indexOf(term.replace(/^v/, '')) !== -1;
        });
    };

    // Biz events can be built on templates / collections still in progress
    // (DRAFT, PENDING_REVIEW, …) — only retired ones are off-limits. Publishing
    // the biz event itself still requires both to be PUBLISHED (enforced server-side).
    // `publishedTemplates` keeps its name but holds every selectable template version.
    function _isSelectableStatus(status) {
        return status !== 'RETIRED' && status !== 'DECOMMISSIONED';
    }


    ctrl.selectCollection = function (collection) {
        ctrl.browsingCollection  = collection;
        ctrl.operations          = [];
        ctrl.displayedOperations = [];
        ctrl.operationSearch     = '';

        var loadOps = function (col) {
            var ops = col.operations
                ? ctrl._opsFromMetadata(col.operations)
                : ctrl._parseOperations(col.swagger);
            ctrl.operations          = ops;
            ctrl.displayedOperations = ops;
            ctrl.loadingOperations   = false;

            var entry = ctrl.scopedCollections.find(function (s) {
                return s.collection.collection_id === col.collection_id;
            });
            if (entry) {
                entry.collection = col;
                entry.operations = ops;
            }
            if (ctrl._pendingScopeCollectionId === col.collection_id) {
                ctrl._pendingScopeCollectionId = null;
                if (!entry) { ctrl.scopedCollections.push({ collection: col, operations: ops }); }
            }
        };

        if (!collection.swagger && !collection.operations) {
            ctrl.loadingOperations = true;
            ApiCollectionService.getById(collection.collection_id)
                .then(function (response) {
                    ctrl.browsingCollection = response.data || response;
                    loadOps(ctrl.browsingCollection);
                })
                .catch(function (err) {
                    console.error('[EditBizEventVersion] Error fetching collection:', err);
                    ctrl.loadingOperations = false;
                });
        } else {
            ctrl.loadingOperations = true;
            $timeout(function () { loadOps(collection); }, 0);
        }
    };

    ctrl.isBrowsingCollection = function (col) {
        return ctrl.browsingCollection &&
               ctrl.browsingCollection.collection_id === col.collection_id;
    };

    ctrl.isScopedCollection = function (col) {
        return ctrl.scopedCollections.some(function (s) {
            return s.collection.collection_id === col.collection_id;
        });
    };

    ctrl.removeScopedCollection = function (col, $event) {
        if ($event) { $event.stopPropagation(); }
        ctrl.scopedCollections = ctrl.scopedCollections.filter(function (s) {
            return s.collection.collection_id !== col.collection_id;
        });
        ctrl.selectedOperations = ctrl.selectedOperations.filter(function (s) {
            return s.collection.collection_id !== col.collection_id;
        });
        if (ctrl.browsingCollection &&
            ctrl.browsingCollection.collection_id === col.collection_id) {
            ctrl.browsingCollection  = null;
            ctrl.operations          = [];
            ctrl.displayedOperations = [];
        }
        ctrl._intelliSenseMappings = null;
        ctrl._recomputeDirty();
    };

    ctrl.toggleScopedCollection = function (col, $event) {
        if ($event) { $event.stopPropagation(); }
        if (ctrl.isScopedCollection(col)) { ctrl.removeScopedCollection(col, $event); return; }

        ctrl._intelliSenseMappings = null;

        var alreadyBrowsed = ctrl.browsingCollection &&
                             ctrl.browsingCollection.collection_id === col.collection_id &&
                             !ctrl.loadingOperations;
        if (alreadyBrowsed) {
            ctrl.scopedCollections.push({ collection: ctrl.browsingCollection, operations: ctrl.operations });
        } else {
            ctrl._pendingScopeCollectionId = col.collection_id;
            ctrl.selectCollection(col);
        }
    };

    // ── Build operations from pre-parsed metadata ─────────────────────────────

    ctrl._opsFromMetadata = function (operations) {
        if (!operations || !operations.length) { return []; }
        return operations.map(function (op, i) {
            return {
                _index: i,
                _pathKey: op.path,
                _method: (op.method || '').toLowerCase(),
                method: (op.method || '').toUpperCase(),
                path: op.path,
                operationId: op.operation_id || null,
                operationName: op.operation_name || null,
                summary: op.summary || null,
                description: op.description || null,
                tags: op.tags || [],
                signature: op.signature || op.path,
                queryParams: op.query_params || [],
                uriParams: op.uri_params || [],
                httpHeaders: op.http_headers || [],
                payload: op.payload || null
            };
        });
    };

    var HTTP_METHODS = ['get', 'post', 'put', 'delete', 'patch', 'options', 'head'];
    var METHOD_ORDER = ['delete', 'get', 'head', 'options', 'patch', 'post', 'put'];

    ctrl._parseOperations = function (swagger) {
        if (!swagger || !swagger.paths) { return []; }
        var ops = [], index = 0;
        Object.keys(swagger.paths).forEach(function (path) {
            var pathItem = swagger.paths[path];
            HTTP_METHODS.forEach(function (method) {
                if (!pathItem[method]) { return; }
                var op = pathItem[method];
                ops.push({
                    _index: index++, _pathKey: path, _method: method, method: method.toUpperCase(),
                    path: path, operationId: op.operationId || null, summary: op.summary || null,
                    description: op.description || null, tags: op.tags || [],
                    queryParams: [], uriParams: [], httpHeaders: []
                });
            });
        });
        ops.sort(function (a, b) {
            var mA = METHOD_ORDER.indexOf(a._method), mB = METHOD_ORDER.indexOf(b._method);
            if (mA !== mB) { return mA - mB; }
            var nA = (a.operationId || a.summary || a.path || '').toLowerCase();
            var nB = (b.operationId || b.summary || b.path || '').toLowerCase();
            return nA < nB ? -1 : nA > nB ? 1 : 0;
        });
        return ops;
    };

    // ── Operation search ─────────────────────────────────────────────────────

    ctrl._applyOperationSearch = function () {
        if (!ctrl.operationSearch || !ctrl.operationSearch.trim()) {
            ctrl.displayedOperations = ctrl.operations;
            return;
        }
        var term = ctrl.operationSearch.trim().toLowerCase();
        ctrl.displayedOperations = ctrl.operations.filter(function (op) {
            return (op.operationName || '').toLowerCase().indexOf(term) !== -1 ||
                   (op.summary      || '').toLowerCase().indexOf(term) !== -1 ||
                   (op.description  || '').toLowerCase().indexOf(term) !== -1 ||
                   (op.path         || '').toLowerCase().indexOf(term) !== -1;
        });
    };

    $scope.$watch(function () { return ctrl.operationSearch; }, function () { ctrl._applyOperationSearch(); });
    $scope.$watch(function () { return ctrl.operations; }, function () {
        ctrl.operationSearch = '';
        ctrl.displayedOperations = ctrl.operations;
    });

    // ── Multi-select operations ───────────────────────────────────────────────

    ctrl.isOperationSelected = function (op) {
        if (!ctrl.browsingCollection) { return false; }
        return ctrl.selectedOperations.some(function (s) {
            return s.op._pathKey === op._pathKey &&
                   s.op._method  === op._method  &&
                   s.collection.collection_id === ctrl.browsingCollection.collection_id;
        });
    };

    ctrl.toggleOperation = function (op) {
        if (ctrl.isOperationSelected(op)) { ctrl._removeOperation(op, ctrl.browsingCollection); }
        else                              { ctrl._addOperation(op, ctrl.browsingCollection); }
        ctrl._recomputeDirty();
    };


    ctrl._addOperation = function (op, collection) {
        ctrl._intelliSenseMappings = null;
        var optionsByType = {};
        ctrl.correlationSourceTypes.forEach(function (cst) {
            var opts = _computeCorrelationOptions(op, cst.id);
            opts.sort(function (a, b) { return a.name < b.name ? -1 : a.name > b.name ? 1 : 0; });
            optionsByType[cst.id] = opts;
        });

        // Restore this operation's original config if it was part of the version
        // being edited — otherwise re-adding an operation you just removed resets
        // it to blank defaults instead of putting it back the way it was, which
        // makes the dirty-check keep reporting a change for what looks, to the
        // user, like a no-op remove-then-re-add.
        var origKey = (collection.collection_id || '') + '|' + (op.operationId || op.operationName || '');
        var origSrc = ctrl._originalSourcesByKey[origKey];

        ctrl.selectedOperations.push({
            op:               op,
            collection:       collection,
            interceptionMode: origSrc ? (origSrc.event_interception_mode || 'INBOUND') : 'INBOUND',
            cardinality:      origSrc ? (origSrc.cardinality || 'ONE') : 'ONE',
            correlationKey:   origSrc && origSrc.correlation_key
                ? { sourceType: origSrc.correlation_key.source_type, sourcePath: origSrc.correlation_key.source_path }
                : { sourceType: null, sourcePath: '' },
            filters:          origSrc
                ? (origSrc.filters || []).map(function (f) {
                    return {
                        filterType:    f.filter_type,
                        sourcePath:    f.source_path,
                        allowedValues: f.allowed_values || [],
                        caseSensitive: f.case_sensitive !== false,
                        _newValue:     '',
                        _open:         false
                    };
                })
                : [],
            _optionsByType:   optionsByType,
            _configOpen:      true
        });

        ctrl.selectedOperations.sort(function (a, b) {
            var colA = (a.collection.collection_name || '').toLowerCase();
            var colB = (b.collection.collection_name || '').toLowerCase();
            if (colA !== colB) { return colA < colB ? -1 : 1; }
            var opA = (a.op.operationName || a.op.summary || a.op.path || '').toLowerCase();
            var opB = (b.op.operationName || b.op.summary || b.op.path || '').toLowerCase();
            return opA < opB ? -1 : opA > opB ? 1 : 0;
        });
    };

    ctrl._removeOperation = function (op, collection) {
        // Partial invalidation: remove only IS mappings referencing this operation_id.
        // Remaining mappings for other ops stay valid.
        var removedOpId = String(op.operationId || op.operation_id || op.id || '');
        _pruneIntelliSenseMappingsForOp(removedOpId);

        ctrl.selectedOperations = ctrl.selectedOperations.filter(function (s) {
            return !(s.op._pathKey === op._pathKey && s.op._method === op._method &&
                     s.collection.collection_id === collection.collection_id);
        });
    };

    ctrl.removeSelectedOperation = function (entry) {
        var removedIdx = ctrl.selectedOperations.indexOf(entry);
        var hasMappings = ctrl.mappings && ctrl.mappings.some(function (m) {
            return m.source && m.source.opIdx === removedIdx;
        });

        if (hasMappings) {
            var opName = (entry.op && (entry.op.operationName || entry.op.summary || entry.op.path)) || 'this operation';
            dialogService.showDecisionDialog(
                'Remove Operation',
                'The operation <strong>' + opName + '</strong> already has field mappings configured. ' +
                'If you proceed, those mappings will be permanently removed.'
            ).then(function (decision) {
                if (decision === 'proceed') { _doRemoveSelectedOperation(entry, removedIdx); }
            });
        } else {
            _doRemoveSelectedOperation(entry, removedIdx);
        }
    };

    function _doRemoveSelectedOperation(entry, removedIdx) {
        ctrl.selectedOperations = ctrl.selectedOperations.filter(function (s) { return s !== entry; });
        ctrl.mappings = ctrl.mappings.filter(function (m) {
            return !(m.source && m.source.opIdx === removedIdx);
        });
        ctrl.mappings.forEach(function (m) {
            _reindexSource(m.source, removedIdx);
            if (m.columnMappings) {
                Object.keys(m.columnMappings).forEach(function (col) {
                    var cm = m.columnMappings[col];
                    if (cm && cm.source) { _reindexSource(cm.source, removedIdx); }
                });
            }
        });
        // 3. Prune _intelliSenseMappings for the removed operation only.
        var removedOpId = String((entry.op.operationId || entry.op.operation_id || entry.op.id) || '');
        _pruneIntelliSenseMappingsForOp(removedOpId);
        ctrl._recomputeDirty();
    }

    function _reindexSource(source, removedIdx) {
        if (!source || source.opIdx <= removedIdx) { return; }
        source.opIdx--;
        if (source.id) {
            source.id = source.id.replace(/^op\d+_/, 'op' + source.opIdx + '_');
        }
    }

    // Helper: remove all sources referencing a specific operation_id from the
    // raw _intelliSenseMappings array. Component entries that end up with zero
    // sources after pruning are also dropped. If the array becomes empty it is
    // set to null so the preload watcher short-circuits correctly.
    function _pruneIntelliSenseMappingsForOp(removedOpId) {
        if (!ctrl._intelliSenseMappings || !removedOpId) { return; }
        ctrl._intelliSenseMappings = ctrl._intelliSenseMappings
            .map(function (componentMapping) {
                var filteredSources = (componentMapping.sources || []).filter(function (src) {
                    return String(src.operation_id || '') !== removedOpId;
                });
                return { frame_component_id: componentMapping.frame_component_id,
                         sources: filteredSources };
            })
            .filter(function (componentMapping) {
                return componentMapping.sources.length > 0;
            });
        if (ctrl._intelliSenseMappings.length === 0) {
            ctrl._intelliSenseMappings = null;
        }
    }

    ctrl.clearAllSelectedOperations = function () {
        var hasMappings = ctrl.mappings && ctrl.mappings.length > 0;
        function _doClearAll() {
            ctrl.selectedOperations    = [];
            ctrl._intelliSenseMappings = null;
            ctrl.mappings              = [];
            ctrl._recomputeDirty();
        }
        if (hasMappings) {
            dialogService.showDecisionDialog(
                'Clear All Operations',
                'All selected operations have active field mappings configured. ' +
                'If you proceed, <strong>all operations and their mappings will be permanently removed</strong>. ' +
                'Do you want to continue?'
            ).then(function (decision) {
                if (decision === 'proceed') { _doClearAll(); }
            });
        } else {
            _doClearAll();
        }
    };

    ctrl.toggleCorrelationConfig = function (entry) { entry._configOpen = !entry._configOpen; };
    ctrl.isCorrelationConfigured = function (entry) {
        return !!(entry.correlationKey.sourceType && entry.correlationKey.sourcePath && entry.correlationKey.sourcePath.trim());
    };
    ctrl.allOperationsConfigured = function () {
        if (ctrl.selectedOperations.length === 0) { return false; }
        return ctrl.selectedOperations.every(function (s) { return ctrl.isCorrelationConfigured(s); });
    };
    ctrl.selectInterceptionMode = function (entry, modeId) {
        entry.interceptionMode = modeId;
        ctrl._recomputeDirty();
    };
    ctrl.onCardinalityChange = function () {
        ctrl._recomputeDirty();
    };
    ctrl.selectCorrelationSource = function (entry, sourceTypeId) {
        if (entry.correlationKey.sourceType === sourceTypeId) { return; }
        entry.correlationKey.sourceType = sourceTypeId;
        entry.correlationKey.sourcePath = '';
        ctrl._recomputeDirty();
    };
    ctrl.getSelectedSourceType = function (entry) {
        if (!entry || !entry.correlationKey.sourceType) { return null; }
        return ctrl.correlationSourceTypes.find(function (cst) {
            return cst.id === entry.correlationKey.sourceType;
        }) || null;
    };
    ctrl.getInterceptionModeLabel = function (modeId) {
        var mode = ctrl.interceptionModes.find(function (m) { return m.id === modeId; });
        return mode ? mode.label : modeId;
    };

    ctrl.interceptionModes = [
        { id: 'INBOUND',          label: 'Inbound',           icon: 'call_received', description: 'Capture events arriving into your system.' },
        { id: 'OUTBOUND',         label: 'Outbound',          icon: 'call_made',     description: 'Capture events sent to external destinations.' },
        { id: 'INBOUND_OUTBOUND', label: 'Inbound & Outbound',icon: 'swap_horiz',    description: 'Capture events in both directions.' }
    ];

    // Expected invocations of the operation per business transaction.
    // MANY turns the whole operation into an ARRAY of N occurrences in the
    // mapping step ({Op:Section[]}.$.path refs, NESTED_TABLE one row/invocation).
    ctrl.cardinalities = [
        { id: 'ONE',  label: 'One',  hint: '1:1 — a single invocation per biz event' },
        { id: 'MANY', label: 'Many', hint: '1:N — N invocations per biz event, captured as an array' }
    ];

    ctrl.correlationSourceTypes = [
        { id: 'HEADER',         label: 'HTTP Header',    icon: 'http' },
        { id: 'CUSTOM_HEADER',  label: 'Custom Header',  icon: 'edit' },
        { id: 'URI_PARAM',      label: 'URI Parameter',  icon: 'link' },
        { id: 'QUERY_PARAM',    label: 'Query Parameter',icon: 'tune' },
        { id: 'REQUEST_FIELD',  label: 'Request Field',  icon: 'arrow_upward' },
        { id: 'RESPONSE_FIELD', label: 'Response Field', icon: 'arrow_downward' }
    ];

    ctrl.httpStatusDescription = require('../../../../common/HttpStatusDescriptions.js');
    ctrl.httpStatusCodes = Array.from({ length: 500 }, function (_, i) { return String(100 + i); });
    ctrl.filterSourceTypes = [
        { id: 'STATUS_CODE', label: 'HTTP Status Code', icon: 'http' },
        { id: 'HEADER',         label: 'HTTP Header',    icon: 'http' },
        { id: 'CUSTOM_HEADER',  label: 'Custom Header',  icon: 'edit' },
        { id: 'URI_PARAM',      label: 'URI Parameter',  icon: 'link' },
        { id: 'QUERY_PARAM',    label: 'Query Parameter',icon: 'tune' },
        { id: 'REQUEST_FIELD',  label: 'Request Field',  icon: 'arrow_upward' },
        { id: 'RESPONSE_FIELD', label: 'Response Field', icon: 'arrow_downward' }
    ];

    // ── Correlation picker ────────────────────────────────────────────────────

    ctrl.openCorrelationPicker = function (entry, sourceType) {
        if (!sourceType) { return; }
        ctrl.selectCorrelationSource(entry, sourceType.id);

        if (sourceType.id === 'REQUEST_FIELD' || sourceType.id === 'RESPONSE_FIELD') {
            var jsonSample = sourceType.id === 'REQUEST_FIELD'
                ? _getRequestSample(entry.op)
                : _getOpResponseSample(entry.op);
            if (!jsonSample) {
                $mdToast.show($mdToast.simple().textContent('No JSON sample available for this operation.').position('top right').theme('warn').hideDelay(3000));
                return;
            }
            ctrl.jsonPathSelector = {
                open: true, entry: entry, sourceType: sourceType,
                attribute: { name: entry.op.operationName || entry.op.summary || entry.op.path },
                jsonSample: jsonSample, currentPath: entry.correlationKey.sourcePath || null,
                mappingType: sourceType.id, target: 'correlation', filter: null
            };
            return;
        }

        var options = entry._optionsByType[sourceType.id] || [];
        if (options.length === 0) { return; }

        $mdDialog.show({
            controller: 'CorrelationKeyPickerController', controllerAs: 'ctrl',
            template: _CORRELATION_PICKER_TEMPLATE, parent: angular.element(document.body),
            clickOutsideToClose: true, escapeToClose: true, multiple: true,
            locals: {
                sourceType: sourceType, options: options, currentValue: entry.correlationKey.sourcePath,
                operationName: entry.op.operationName || entry.op.summary || entry.op.path
            }
        }).then(function (selectedValue) {
            entry.correlationKey.sourcePath = selectedValue;
            // _recomputeDirty(), not _markDirty(): picking a correlation value is
            // part of curSourceKeys' comparison (sourceType:sourcePath), so it can
            // correctly un-dirty if the user picks the same value the operation
            // already had — same fix as _addOperation, same one-way-latch mistake.
            ctrl._recomputeDirty();
        }).catch(angular.noop);
    };

    ctrl.jsonPathSelector = { open: false, entry: null, sourceType: null, attribute: null,
        jsonSample: null, currentPath: null, mappingType: null, target: 'correlation', filter: null };

    ctrl.onCorrelationJsonPathSave = function (selectedPath) {
        if (ctrl.jsonPathSelector.target === 'filter') {
            var f = ctrl.jsonPathSelector.filter;
            if (f) { f.sourcePath = selectedPath; }
        } else {
            var entry = ctrl.jsonPathSelector.entry;
            if (entry) { entry.correlationKey.sourcePath = selectedPath; }
        }
        ctrl.jsonPathSelector.open = false;
        ctrl._recomputeDirty();
    };

    ctrl.onCorrelationJsonPathCancel = function () { ctrl.jsonPathSelector.open = false; };

    // ── Filter management ─────────────────────────────────────────────────────

    ctrl.addFilter = function (entry) {
        entry.filters.push({ filterType: null, sourcePath: '', allowedValues: [], caseSensitive: true, _newValue: '', _open: true });
        ctrl._recomputeDirty();
    };
    ctrl.removeFilter = function (entry, idx) { entry.filters.splice(idx, 1); ctrl._recomputeDirty(); };
    ctrl.toggleFilter = function (filter) { filter._open = !filter._open; };
    ctrl.toggleAllFilters = function (entry) {
        var anyOpen = entry.filters.some(function (f) { return f._open; });
        entry.filters.forEach(function (f) { f._open = !anyOpen; });
    };
    ctrl.allFiltersOpen = function (entry) {
        return entry.filters.length > 0 && entry.filters.every(function (f) { return f._open; });
    };
    ctrl.selectFilterSourceType = function (entry, filter, sourceTypeObj) {
        if (filter.filterType === sourceTypeObj.id) { return; }
        filter.filterType = sourceTypeObj.id;
        filter.sourcePath = '';
        ctrl._recomputeDirty();
    };
    ctrl.openFilterSourcePicker = function (entry, filter, sourceTypeObj) {
        var prevType = filter.filterType;
        filter.filterType = sourceTypeObj.id;
        if (prevType !== sourceTypeObj.id) { filter.sourcePath = ''; }
        if (sourceTypeObj.id === 'STATUS_CODE') {
            filter.sourcePath = 'status';
            if (prevType !== 'STATUS_CODE') filter.allowedValues = [];
            return;
        }
        if (sourceTypeObj.id === 'CUSTOM_HEADER') { return; }
        if (sourceTypeObj.id === 'REQUEST_FIELD' || sourceTypeObj.id === 'RESPONSE_FIELD') {
            ctrl.openFilterJsonPathSelector(entry, filter);
            return;
        }
        var options = (entry._optionsByType && entry._optionsByType[sourceTypeObj.id]) || [];
        if (options.length === 0) { return; }
        $mdDialog.show({
            controller: 'CorrelationKeyPickerController', controllerAs: 'ctrl',
            template: _CORRELATION_PICKER_TEMPLATE, parent: angular.element(document.body),
            clickOutsideToClose: true, escapeToClose: true, multiple: true,
            locals: {
                sourceType: sourceTypeObj, options: options, currentValue: filter.sourcePath,
                operationName: entry.op.operationName || entry.op.summary || entry.op.path
            }
        }).then(function (selectedValue) {
            filter.sourcePath = selectedValue;
            ctrl._recomputeDirty();
        }).catch(angular.noop);
    };
    ctrl.hasFilterPickerOptions = function (entry, sourceTypeId) {
        if (!entry || !sourceTypeId) { return false; }
        if (sourceTypeId === 'STATUS_CODE' || sourceTypeId === 'CUSTOM_HEADER' || sourceTypeId === 'REQUEST_FIELD' || sourceTypeId === 'RESPONSE_FIELD') { return false; }
        return ((entry._optionsByType && entry._optionsByType[sourceTypeId]) || []).length > 0;
    };
    ctrl.hasFilterOptions = function (entry, sourceTypeId) {
        if (!entry) { return false; }
        if (sourceTypeId === 'STATUS_CODE') { return true; }
        if (sourceTypeId === 'CUSTOM_HEADER')   { return true; }
        if (sourceTypeId === 'REQUEST_FIELD')   { return !!_getRequestSample(entry.op); }
        if (sourceTypeId === 'RESPONSE_FIELD')  { return !!_getOpResponseSample(entry.op); }
        return ((entry._optionsByType && entry._optionsByType[sourceTypeId]) || []).length > 0;
    };
    ctrl.getFilterSourceType = function (filter) {
        if (!filter || !filter.filterType) { return null; }
        return ctrl.filterSourceTypes.find(function (fst) { return fst.id === filter.filterType; }) || null;
    };
    ctrl.addFilterValue = function (filter) {
        var val = (filter._newValue || '').trim();
        if (!val) { return; }
        if (filter.filterType === 'STATUS_CODE' && !/^[1-5][0-9]{2}$/.test(val)) return;
        if (filter.allowedValues.indexOf(val) === -1) { filter.allowedValues.push(val); }
        filter._newValue = '';
        ctrl._recomputeDirty();
    };
    ctrl.statusCodeSuggestions = function (filter) {
        var query = (filter._newValue || '').trim().toLowerCase();
        return ctrl.httpStatusCodes.filter(function (code) {
            var matches = code.indexOf(query) === 0 || ctrl.httpStatusDescription(code).toLowerCase().indexOf(query) !== -1;
            return matches && filter.allowedValues.indexOf(code) === -1;
        });
    };

    ctrl.selectStatusCode = function (filter, code) {
        if (!code) return;
        filter._newValue = code;
        ctrl.addFilterValue(filter);
        filter._selectedStatus = null;
    };

    ctrl.removeFilterValue = function (filter, idx) { filter.allowedValues.splice(idx, 1); ctrl._recomputeDirty(); };

    var _PILL_COLORS = ['#607d8b','#8bc34a','#03a9f4','#ff9800','#e91e63','#9c27b0','#009688','#cddc39','#ffc107','#795548'];
    ctrl.getPillBg = function (val) {
        var s = val || '', hash = 0;
        for (var i = 0; i < s.length; i++) { hash = s.charCodeAt(i) + ((hash << 5) - hash); }
        return _PILL_COLORS[Math.abs(hash) % _PILL_COLORS.length];
    };
    ctrl.getPillColor = function (val) {
        var hex = ctrl.getPillBg(val);
        var r = parseInt(hex.substr(1,2),16), g = parseInt(hex.substr(3,2),16), b = parseInt(hex.substr(5,2),16);
        return (0.299*r + 0.587*g + 0.114*b) > 140 ? '#000000' : '#ffffff';
    };
    ctrl.onFilterValueKeydown = function ($event, filter) {
        if ($event.key === ' ' || $event.key === 'Enter') { $event.preventDefault(); ctrl.addFilterValue(filter); }
    };
    ctrl.isFilterFreeText = function (filter) {
        return filter.filterType && filter.filterType !== 'STATUS_CODE' && filter.filterType !== 'REQUEST_FIELD' && filter.filterType !== 'RESPONSE_FIELD';
    };
    ctrl.openFilterJsonPathSelector = function (entry, filter) {
        var sampleFn = filter.filterType === 'REQUEST_FIELD' ? _getRequestSample : _getOpResponseSample;
        var jsonSample = sampleFn(entry.op);
        if (!jsonSample) {
            $mdToast.show($mdToast.simple().textContent('No JSON sample available.').position('top right').theme('warn').hideDelay(3000));
            return;
        }
        ctrl.jsonPathSelector = {
            open: true, entry: entry, sourceType: null,
            attribute: { name: entry.op.operationName || entry.op.summary || entry.op.path },
            jsonSample: jsonSample, currentPath: filter.sourcePath || null,
            mappingType: filter.filterType, target: 'filter', filter: filter
        };
    };
    ctrl.hasCorrelationOptions = function (entry, sourceTypeId) {
        if (!entry) { return false; }
        if (sourceTypeId === 'CUSTOM_HEADER')  { return true; }
        if (sourceTypeId === 'REQUEST_FIELD')  { return !!_getRequestSample(entry.op); }
        if (sourceTypeId === 'RESPONSE_FIELD') { return !!_getOpResponseSample(entry.op); }
        if (!entry._optionsByType) { return false; }
        return (entry._optionsByType[sourceTypeId] || []).length > 0;
    };
    ctrl.getCorrelationOptions = function (entry) {
        if (!entry || !entry.correlationKey || !entry.correlationKey.sourceType) { return []; }
        if (entry._optionsByType) { return entry._optionsByType[entry.correlationKey.sourceType] || []; }
        return _computeCorrelationOptions(entry.op, entry.correlationKey.sourceType);
    };

    // ── JSON viewer ───────────────────────────────────────────────────────────
    ctrl.jsonViewer = { open: false, json: null, title: null };
    ctrl.hasRequestSample  = function (op) { return !!(op.payload && op.payload.request && op.payload.request.json_sample); };
    ctrl.hasResponseSample = function (op) { return !!ctrl._getResponseSample(op); };
    ctrl._getResponseSample = function (op) {
        if (!op.payload || !op.payload.responses) { return null; }
        var responses = op.payload.responses;
        var preferred = ['200','201','202'];
        for (var i = 0; i < preferred.length; i++) {
            var r = responses[preferred[i]];
            if (r && r.json_sample) { return r.json_sample; }
        }
        var keys = Object.keys(responses);
        for (var j = 0; j < keys.length; j++) {
            if (keys[j].startsWith('2') && responses[keys[j]].json_sample) { return responses[keys[j]].json_sample; }
        }
        return null;
    };
    ctrl.openJsonSampleViewer = function (op, side, $event) {
        if ($event) { $event.stopPropagation(); }
        var json = side === 'request' ? op.payload.request.json_sample : ctrl._getResponseSample(op);
        if (!json) { return; }
        ctrl.jsonViewer = {
            open: true, json: json,
            title: (side === 'request' ? 'Request' : 'Response') + ' — ' + (op.operationName || op.summary || op.path)
        };
    };
    ctrl.closeJsonViewer = function () { ctrl.jsonViewer.open = false; };

    // ── Operation detail popup ────────────────────────────────────────────────
    ctrl.openOperationDetail = function (op, $event) {
        if ($event) { $event.stopPropagation(); }
        var swagger = ctrl.browsingCollection && ctrl.browsingCollection.swagger;
        if (!swagger) { return; }
        $mdDialog.show({
            controller: 'BizEventOperationDetailController', controllerAs: 'ctrl',
            templateUrl: 'profile/tabs/biz-events/create-wizard/operation-detail-dialog.html',
            parent: angular.element(document.body), clickOutsideToClose: true, escapeToClose: true, multiple: true,
            locals: { operation: op, filteredSpec: _buildSingleOpSpec(swagger, op._pathKey, op._method) }
        }).catch(angular.noop);
    };

    function _buildSingleOpSpec(swagger, pathKey, method) {
        var filtered = angular.copy(swagger);
        var singlePath = {};
        singlePath[pathKey] = {};
        singlePath[pathKey][method] = swagger.paths[pathKey][method];
        if (swagger.paths[pathKey].parameters) { singlePath[pathKey].parameters = swagger.paths[pathKey].parameters; }
        filtered.paths = singlePath;
        return filtered;
    }

    // ── Correlation helpers ───────────────────────────────────────────────────

    function _computeCorrelationOptions(op, sourceType) {
        if (!op || !sourceType) { return []; }
        switch (sourceType) {
            case 'HEADER':
                return (op.httpHeaders || []).map(function (p) {
                    return { value: p.name, name: p.name, description: p.description || null, type: p.type || null, path: null, required: !!p.required };
                });
            case 'URI_PARAM':
                return (op.uriParams || []).map(function (p) {
                    return { value: p.name, name: p.name, description: p.description || null, type: p.type || 'string', path: null, required: !!p.required };
                });
            case 'QUERY_PARAM':
                return (op.queryParams || []).map(function (p) {
                    return { value: p.name, name: p.name, description: p.description || null, type: p.type || 'string', path: null, required: !!p.required };
                });
            case 'REQUEST_FIELD':  return _payloadFieldOptions(op, 'request');
            case 'RESPONSE_FIELD': return _payloadFieldOptions(op, 'response');
            default: return [];
        }
    }

    function _payloadFieldOptions(op, side) {
        var payload = op.payload;
        if (!payload) { return []; }
        var body = null;
        if (side === 'request') {
            body = payload.request;
        } else {
            var responses = payload.responses || {};
            body = responses['200'] || responses['201'] || responses['202'] || null;
            if (!body || !body.fields || !body.fields.length) {
                var keys = Object.keys(responses);
                for (var i = 0; i < keys.length; i++) {
                    var c = responses[keys[i]];
                    if (c && c.fields && c.fields.length > 0) { body = c; break; }
                }
            }
        }
        if (!body || !body.fields || !body.fields.length) { return []; }
        return body.fields
            .filter(function (f) {
                if (!f.path || f.path === '$') { return false; }
                var t = (f.type || 'string').toLowerCase();
                return t !== 'object' && t !== 'array';
            })
            .map(function (f) {
                return {
                    value: f.path, name: f.name || f.path,
                    description: f.description || null,
                    type: f.format ? (f.type + '/' + f.format) : (f.type || 'string'),
                    path: f.path, required: !!f.required
                };
            });
    }

    function _getRequestSample(op) {
        return (op.payload && op.payload.request && op.payload.request.json_sample)
            ? op.payload.request.json_sample : null;
    }

    function _getOpResponseSample(op) {
        if (!op.payload || !op.payload.responses) { return null; }
        var responses = op.payload.responses;
        var preferred = ['200','201','202'];
        for (var i = 0; i < preferred.length; i++) {
            if (responses[preferred[i]] && responses[preferred[i]].json_sample) { return responses[preferred[i]].json_sample; }
        }
        var keys = Object.keys(responses);
        for (var j = 0; j < keys.length; j++) {
            if (keys[j].charAt(0) === '2' && responses[keys[j]].json_sample) { return responses[keys[j]].json_sample; }
        }
        return null;
    }

    // ── Correlation picker template ───────────────────────────────────────────

    var _CORRELATION_PICKER_TEMPLATE = [
        '<md-dialog class="ckp-dialog" aria-label="Select Correlation Key">',
        '  <md-toolbar class="ckp-toolbar"><div class="md-toolbar-tools">',
        '    <md-icon class="ckp-toolbar-icon">{{ ctrl.sourceType.icon }}</md-icon>',
        '    <div class="ckp-header-info">',
        '      <span class="ckp-title">{{ ctrl.sourceType.label }}</span>',
        '      <span class="ckp-subtitle">{{ ctrl.operationName }}</span>',
        '    </div>',
        '    <span flex></span>',
        '    <md-button class="md-icon-button ckp-close-btn" ng-click="ctrl.cancel()" aria-label="Close">',
        '      <md-icon>close</md-icon>',
        '    </md-button>',
        '  </div></md-toolbar>',
        '  <md-dialog-content class="ckp-content">',
        '    <div class="ckp-search-wrap">',
        '      <md-icon class="ckp-search-icon">search</md-icon>',
        '      <input type="text" class="ckp-search-input" ng-model="ctrl.search" placeholder="Search…" autocomplete="off" />',
        '      <md-icon class="ckp-clear-icon" ng-if="ctrl.search" ng-click="ctrl.search = \'\'">close</md-icon>',
        '    </div>',
        '    <div class="ckp-options">',
        '      <div ng-repeat="opt in ctrl.filteredOptions() track by opt.value" class="ckp-option"',
        '           ng-class="{\'ckp-option--selected\': ctrl.currentValue === opt.value}" ng-click="ctrl.select(opt)">',
        '        <div class="ckp-option-radio"><div class="ckp-option-radio-dot"></div></div>',
        '        <div class="ckp-option-body">',
        '          <div class="ckp-option-name-row">',
        '            <span class="ckp-option-name">{{ opt.name }}</span>',
        '            <span class="ckp-option-required" ng-if="opt.required">required</span>',
        '            <span class="ckp-option-type" ng-if="opt.type">{{ opt.type }}</span>',
        '          </div>',
        '          <span class="ckp-option-path" ng-if="opt.path && opt.path !== opt.name">{{ opt.path }}</span>',
        '          <span class="ckp-option-desc" ng-if="opt.description">{{ opt.description }}</span>',
        '        </div>',
        '      </div>',
        '      <div class="ckp-empty" ng-if="ctrl.filteredOptions().length === 0">No results for "{{ ctrl.search }}"</div>',
        '    </div>',
        '  </md-dialog-content>',
        '  <md-dialog-actions class="ckp-footer">',
        '    <span flex></span>',
        '    <md-button class="cbew-btn cbew-btn--cancel" ng-click="ctrl.cancel()">Cancel</md-button>',
        '  </md-dialog-actions>',
        '</md-dialog>'
    ].join('\n');

    // ========================================
    // STEP 6 — FIELD MAPPING
    // ========================================

    ctrl.mappings          = [];
    ctrl._originalApiMappings = [];
    ctrl.targetExpressions = {};
    ctrl.bmelErrors        = false;

    // ========================================
    // WIZARD NAVIGATION
    // ========================================

    ctrl.canAdvance = function () {
        switch (ctrl.currentStep) {
            case ctrl.STEP_TARGET:  return !!ctrl.selectedTemplate && !!ctrl.selectedFrameType;
            case ctrl.STEP_OPS:     return ctrl.selectedOperations.length > 0;
            case ctrl.STEP_CONFIG:  return ctrl.allOperationsConfigured();
            case ctrl.STEP_MAPPING: return ctrl.allRequiredComponentsMapped() && !ctrl.bmelErrors;
            default: return true;
        }
    };

    // Step 4 renders lazily: until it does, ctrl.mappings is empty and the saved
    // mappings only live in ctrl._originalApiMappings. Validating (or saving)
    // ctrl.mappings alone let Apply Changes from steps 1-3 skip the required-
    // component check and send biz_event_mappings: [] — wiping every mapping.
    ctrl._mappingStepLoaded = function () {
        return ctrl._preloadMappingsRef !== null || ctrl._mappingsDirty;
    };

    function _frameComponents() {
        if (!ctrl.selectedFrameType) { return []; }
        return ctrl.selectedFrameType.frame_fields ||
               ctrl.selectedFrameType.components   ||
               ctrl.selectedFrameType.frame_components || [];
    }

    function _componentId(c) {
        return c.frame_component_id || c.frame_field_id || c.component_id || c.id;
    }

    // Saved mappings that still apply to the current target frame and selected
    // operations — what step 4 would load. Used while step 4 hasn't been opened.
    function _carriedOverApiMappings() {
        var compIds = {};
        _frameComponents().forEach(function (c) { compIds[String(_componentId(c))] = true; });
        var opNames = {};
        ctrl.selectedOperations.forEach(function (entry) {
            var op = entry.op;
            [op.operationName, op.operationId, op.summary, op.path].filter(Boolean)
                .forEach(function (n) { opNames[n] = true; });
        });
        return (ctrl._originalApiMappings || []).filter(function (m) {
            var tgt = m.target || {};
            if (!tgt.component_id || !compIds[String(tgt.component_id)]) { return false; }
            return (m.sources || []).every(function (src) { return opNames[src.operation_name || '']; });
        });
    }

    function _mappedComponentIds() {
        var ids = {};
        if (ctrl._mappingStepLoaded()) {
            (ctrl.mappings || []).forEach(function (m) { ids[String(m.targetComponentId)] = true; });
        } else {
            _carriedOverApiMappings().forEach(function (m) { ids[String(m.target.component_id)] = true; });
        }
        return ids;
    }

    ctrl.allRequiredComponentsMapped = function () {
        if (!ctrl.selectedFrameType) { return false; }
        return ctrl.unmappedRequiredCount() === 0;
    };

    ctrl.unmappedRequiredCount = function () {
        if (!ctrl.selectedFrameType) { return 0; }
        var mapped = _mappedComponentIds();
        return _frameComponents().filter(function (c) {
            return c.is_required && !mapped[String(_componentId(c))];
        }).length;
    };

    // Leaving Configure Operations: operations and cardinalities are final, so
    // this is the single point where the agent maps. The template-change remap
    // already runs the agent over every operation, so it covers a pending
    // IntelliSense selection too.
    ctrl.nextStep = function () {
        if (!ctrl.canAdvance()) { return; }
        if (ctrl.currentStep === ctrl.STEP_CONFIG && ctrl.selectedOperations.length
                && (ctrl._remapPending || _agentSelectionNeedsMapping() || _agentRemapNeeded())) {
            var run = ctrl._remapPending ? _remapMovedOperations : _runAgentMapping;
            ctrl._remapPending = false;
            ctrl._agentPending = false;
            run().finally(_advanceStep);
            return;
        }
        _advanceStep();
    };

    function _advanceStep() {
        // Lazy-load: entering Step 2 (Select Operations) → ensure collections are loaded
        // Auto-detect common correlation field when entering Configure Operations
        if (ctrl.currentStep === ctrl.STEP_OPS) { ctrl._autoDetectCorrelation(); }
        // Initialize mapping step before entering Step 4
        if (ctrl.currentStep === ctrl.STEP_CONFIG) { ctrl._initMappingStep(); }

        if (ctrl.currentStep < ctrl.totalSteps) { ctrl.currentStep++; }

        // When landing on the mapping step for the first time (no prior user changes),
        // the directive processes preloadMappings and writes ctrl.mappings via two-way
        // binding. That write must not mark the form dirty.
        // We snapshot hasChanges BEFORE advancing and only reset if it was false —
        // preserving dirty state when the user navigated back from mapping after edits.
        // No timeout needed — dirty tracking uses reference identity
        // in the $watchCollection below to distinguish preload from user edits.
    }

    ctrl.prevStep = function () { if (ctrl.currentStep > 1) { ctrl.currentStep--; } };

    // ── Auto-correlation detection ────────────────────────────────────────────
    // Called when advancing from STEP_OPS → STEP_CONFIG.
    // Only pre-fills operations that have not yet been manually configured.
    // Priority: (1) common HTTP Header, (2) common URI Parameter.
    // "Common" means the field name appears in EVERY selected operation's options
    // for that source type.
    ctrl._autoDetectCorrelation = function () {
        var ops = ctrl.selectedOperations;
        if (!ops || ops.length === 0) { return; }

        function _findCommonField(sourceTypeId) {
            var candidates = (ops[0]._optionsByType[sourceTypeId] || []).map(function (o) { return o.value; });
            if (candidates.length === 0) { return null; }
            for (var i = 1; i < ops.length; i++) {
                var available = {};
                (ops[i]._optionsByType[sourceTypeId] || []).forEach(function (o) { available[o.value] = true; });
                candidates = candidates.filter(function (c) { return available[c]; });
                if (candidates.length === 0) { return null; }
            }
            var required = candidates.filter(function (name) {
                var opt = (ops[0]._optionsByType[sourceTypeId] || []).filter(function (o) { return o.value === name; })[0];
                return opt && opt.required;
            });
            return required.length > 0 ? required[0] : candidates[0];
        }

        var bestType  = null;
        var bestField = null;

        var headerField = _findCommonField('HEADER');
        if (headerField) {
            bestType  = 'HEADER';
            bestField = headerField;
        } else {
            var uriField = _findCommonField('URI_PARAM');
            if (uriField) {
                bestType  = 'URI_PARAM';
                bestField = uriField;
            }
        }

        if (!bestType) { return; }

        ops.forEach(function (entry) {
            if (entry.correlationKey.sourceType || entry.correlationKey.sourcePath) { return; }
            entry.correlationKey.sourceType = bestType;
            entry.correlationKey.sourcePath = bestField;
        });
    };

    ctrl._initMappingStep = function () {
        if (!ctrl.mappings || !Array.isArray(ctrl.mappings)) { ctrl.mappings = []; }

        // Pre-load Monaco + BMEL function registry so KNOWN_FN_NAMES is populated
        // before the mapping step's own validation pass and before the BMEL editor
        // is opened for the first time — mirrors CreateBizEventWizardController's
        // _initMappingStep. Without this, BmelEditorService.validateExpression()
        // runs against an empty function registry (since nothing else in the Edit
        // wizard loads it), flags every expression that uses a function as invalid,
        // sets ctrl.bmelErrors = true, and keeps Apply Changes disabled regardless
        // of hasChanges — and the BMEL editor shows every function as unrecognized
        // until it's reopened a second time (after loadFunctions() has resolved).
        Promise.all([
            BmelEditorService.loadMonaco(),
            BmelEditorService.getFunctions().length
                ? Promise.resolve()
                : BmelEditorService.loadFunctions()
        ]).then(function () {
            BmelEditorService.refreshLanguageTokenizer();
        }).catch(angular.noop);
    };

    // ========================================
    // DIRTY TRACKING
    // ========================================

    // _recomputeDirty() compares the current wizard state against the original
    // version snapshot and sets ctrl.hasChanges accordingly.
    // This means "going back" to the original value automatically disables
    // the Apply Changes button — instead of a one-way latch.
    ctrl._recomputeDirty = function () {
        // ── Target frame changed? ──────────────────────────────────────────
        var origFtId = ctrl._originalTarget ? ctrl._originalTarget.frame_type_id : null;
        var curFtId  = ctrl.selectedFrameType
            ? (ctrl.selectedFrameType.frame_type_id || ctrl.selectedFrameType.id)
            : null;
        if (curFtId !== origFtId) { ctrl.hasChanges = true; return; }

        var origTplId = ctrl._originalTarget ? ctrl._originalTarget.template_instance_id : null;
        var curTplId  = ctrl.selectedTemplate ? ctrl.selectedTemplate.id : null;
        if (curTplId !== origTplId) { ctrl.hasChanges = true; return; }

        // ── Operations changed? ────────────────────────────────────────────
        var curSourceKeys = ctrl.selectedOperations.map(function (entry) {
            return (entry.collection.collection_id || '') + '|' +
                   (entry.op.operationId || entry.op.operationName || '') + '|' +
                   (entry.interceptionMode || '') + '|' +
                   (entry.cardinality || 'ONE') + '|' +
                   (entry.correlationKey
                       ? (entry.correlationKey.sourceType || '') + ':' + (entry.correlationKey.sourcePath || '')
                       : '');
        }).sort().join(';;');

        if (curSourceKeys !== ctrl._originalSourceKeys) { ctrl.hasChanges = true; return; }

        // ── Mappings changed? (count proxy — full diff handled by $watchCollection) ──
        // If _preloadMappingsRef is set, mappings were loaded; compare length.
        // A proper field-level diff is handled by the $watchCollection below.
        // This recompute only clears hasChanges when ops+target revert to original.
        if (ctrl._mappingsDirty) { ctrl.hasChanges = true; return; }

        // ── Filters changed? ───────────────────────────────────────────────
        // Full content comparison (filterType/sourcePath/allowedValues/
        // caseSensitive per filter), not just how many filters exist — see
        // _originalFiltersKey above.
        var curFiltersKey = ctrl.selectedOperations.map(function (entry) {
            var opId = (entry.collection.collection_id || '') + '|' +
                       (entry.op.operationId || entry.op.operationName || '');
            var filterKeys = (entry.filters || []).map(_curFilterKey).sort().join(',');
            return opId + '::' + filterKeys;
        }).sort().join(';;');
        if (curFiltersKey !== ctrl._originalFiltersKey) { ctrl.hasChanges = true; return; }

        // All checks passed — state matches original
        ctrl.hasChanges = false;
    };

    // _markDirty is kept as a fast path for actions that are always dirty
    // (e.g. adding a filter value, editing BMEL) where full recompute isn't needed.
    ctrl._markDirty = function () {
        ctrl.hasChanges = true;
    };

    // ── Mapping step dirty tracking ──────────────────────────────────────────
    // We use $watchCollection (shallow) to avoid the O(n×depth) cost of deep watch.
    //
    // The tricky case: when the directive first renders it writes ctrl.mappings
    // synchronously via two-way binding (preload path). That write happens in the
    // first digest after the ng-if creates the directive — before any $timeout.
    // We must not mark dirty for that write.
    //
    // The directive uses all of:
    //   - ctrl.mappings = translated          (preload: replaces array reference)
    //   - ctrl.mappings.push(m)               (add connector: mutates in-place)
    //   - ctrl.mappings = ctrl.mappings.filter(...) (remove: replaces reference)
    //
    // The only write we must ignore is the initial preload. We used to detect it
    // by shape ("old array was empty, new array isn't") but that's ambiguous: a
    // user's first-ever mapping on a version/step that legitimately starts with
    // zero mappings (e.g. after changing Target Frame in Step 1) produces the same
    // 0 → N transition and was being silently swallowed — Apply Changes never
    // enabled until a *second* edit. The directive now tags the preload array
    // itself (`_isPreloadWrite`) so detection here is unambiguous regardless of
    // starting length.
    ctrl._preloadMappingsRef = null;
    ctrl._mappingsDirty      = false;

    // Called by biz-event-mapping-step when the user clicks "Clear All".
    // Same issue as the create wizard: preloadMappings is bound one-way ('<'),
    // so clearing ctrl.mappings in the step never reaches ctrl._preloadMappings
    // here. Without this, Back → Next re-creates the directive and its preload
    // watch repopulates ctrl.mappings from this same stale API data.
    ctrl.discardPreloadMappings = function () {
        ctrl._preloadMappings = null;
    };

    $scope.$watchCollection('ctrl.mappings', function (n, o) {
        if (!ctrl._initialLoadDone) { return; }
        // Detect the preload write via its explicit tag, not array shape —
        // see comment above.
        if (ctrl._preloadMappingsRef === null && n && n._isPreloadWrite) {
            ctrl._preloadMappingsRef = n;  // captured — any future write marks dirty
            delete n._isPreloadWrite;
            return;
        }
        // Every other change (push, filter-replace, clear) is a user edit
        if (n !== o) {
            ctrl._mappingsDirty = true;
            ctrl._markDirty();
        }
    });

    $scope.$watchCollection('ctrl.targetExpressions', function (n, o) {
        if (!ctrl._initialLoadDone) { return; }
        // Skip expression writes that happen during the preload phase
        // (directive populates targetExpressions in the same digest as mappings)
        if (ctrl._preloadMappingsRef === null && !ctrl.hasChanges) { return; }
        if (n !== o) {
            ctrl._mappingsDirty = true;
            ctrl._markDirty();
        }
    });

    // ========================================
    // APPLY CHANGES — PUT request
    // ========================================

    ctrl.applyChanges = function () {
        // Only from the composer, once the mappings and their BMEL have been validated
        if (ctrl.currentStep !== ctrl.STEP_MAPPING) { return; }
        if (!ctrl.hasChanges || ctrl.applying || ctrl.bmelErrors || !ctrl.allRequiredComponentsMapped()) { return; }
        ctrl.applying = true;

        var payload = _buildUpdatePayload();

        BusinessEventsService.update(bizEvent.biz_event_id, payload)
            .then(function () {
                $mdToast.show(
                    $mdToast.simple()
                        .textContent('Business event updated successfully')
                        .position('top right')
                        .hideDelay(3000)
                );
                $mdDialog.hide({ updated: true });
            })
            .catch(function (err) {
                console.error('[EditBizEventVersion] Error updating biz event:', err);
                dialogService.showErrorDialog(
                    'Update Failed',
                    (err.data && err.data.message) || 'Could not update the business event.'
                );
            })
            .finally(function () { ctrl.applying = false; });
    };

    // ── Builds the full UpdateBizEventDTO payload ─────────────────────────────
    function _buildUpdatePayload() {
        return {
            biz_event_name:        ctrl.bizEventName.trim(),
            biz_event_description: ctrl.bizEventDescription || '',
            account_id:            profile.accountId || profile.account_id,
            profile_id:            profile.id,
            biz_event_version:     version.biz_event_version,
            biz_event_trigger: {
                biz_event_trigger_id:          ctrl.triggerType,
                biz_event_trigger_description: ''
            },
            biz_event_sources: ctrl.selectedOperations.map(function (s) {
                return {
                    source_type:             ctrl.selectedSourceType.id,
                    event_interception_mode: s.interceptionMode,
                    cardinality:             s.cardinality || 'ONE',
                    api_collection_id:       s.collection.collection_id,
                    api_collection_name:     s.collection.collection_name,
                    api_collection_version:  s.collection.api_version || null,
                    operation_id:            s.op.operationId,
                    operation_name:          s.op.operationName || s.op.summary || s.op.path,
                    api_method:              s.op.method,
                    api_endpoint:            s.op.path,
                    api_signature:           s.op.signature || s.op.path,
                    correlation_key: {
                        source_type: s.correlationKey.sourceType,
                        source_path: s.correlationKey.sourcePath
                    },
                    filters: (s.filters || [])
                        .filter(function (f) {
                            return f.filterType && f.sourcePath && f.sourcePath.trim() && f.allowedValues.length > 0;
                        })
                        .map(function (f) {
                            return {
                                filter_type:    f.filterType,
                                source_path:    f.sourcePath.trim(),
                                allowed_values: f.allowedValues,
                                case_sensitive: f.caseSensitive
                            };
                        })
                };
            }),
            biz_event_target: {
                template_instance_id:      ctrl.selectedTemplate.id,
                template_instance_version: ctrl.selectedTemplate.version,
                frame_type_id:             ctrl.selectedFrameType.frame_type_id,
                frame_type_name:           ctrl.frameTypeName(ctrl.selectedFrameType)
            },
            biz_event_mappings: ctrl._mappingStepLoaded()
                ? _buildMappingsPayload()
                : _carriedOverApiMappings()
        };
    }

    function _buildMappingsPayload() {
        var result   = [];
        var frameComps = ctrl.selectedFrameType.frame_fields ||
                         ctrl.selectedFrameType.components   ||
                         ctrl.selectedFrameType.frame_components || [];

        function _findFrameComp(compId) {
            return frameComps.find(function (c) {
                var cid = c.frame_component_id || c.frame_field_id || c.component_id || c.id;
                return String(cid) === String(compId);
            }) || null;
        }

        function _makeSource(src) {
            var opEntry = ctrl.selectedOperations[src.opIdx];
            if (!opEntry) { return null; }

            // Split CONTEXT → REQUEST_CONTEXT / RESPONSE_CONTEXT for the backend
            var backendSourceType = src.sourceType;
            if (src.sourceType === 'CONTEXT') {
                var p = src.path || '';
                backendSourceType = (p.indexOf('$bm.response') === 0)
                    ? 'RESPONSE_CONTEXT'
                    : 'REQUEST_CONTEXT';
            }

            return {
                operation_name: opEntry.op.operationName || opEntry.op.summary || opEntry.op.path,
                source_type:    backendSourceType,
                source_path:    src.path,
                placeholder:    null
            };
        }

        ctrl.mappings.forEach(function (m) {
            var mappingSource = _makeSource(m.source);
            if (!mappingSource) { return; }

            var fc       = _findFrameComp(m.targetComponentId);
            var compType = (fc && (fc.frame_component_type || fc.component_type)) || 'FIELD_REFERENCE';
            var compId   = fc ? (fc.frame_component_id || fc.id || fc.component_id || null) : null;
            var attrId   = fc ? (fc.attribute_type_id   || null) : null;
            var attrName = fc ? (fc.attribute_type_name || null) : null;
            var expr     = ctrl.targetExpressions[m.targetComponentId] || m.bmelExpression || null;

            result.push({
                sources:   [mappingSource],
                target: {
                    component_id:   compId,
                    component_name: m.targetComponentName,
                    component_type: compType,
                    attribute_id:   attrId,
                    attribute_name: attrName
                },
                transform: expr ? { expression: expr } : null
            });

            if (m.columnMappings) {
                Object.keys(m.columnMappings).forEach(function (colName) {
                    var cm = m.columnMappings[colName];
                    if (!cm || !cm.source) { return; }
                    var colSource = _makeSource(cm.source);
                    if (!colSource) { return; }
                    var colExpr = ctrl.targetExpressions[m.targetComponentId + '|col|' + colName] || cm.bmelExpression || null;
                    result.push({
                        sources:   [colSource],
                        target: {
                            component_id:   compId,
                            component_name: m.targetComponentName,
                            component_type: 'NESTED_TABLE',
                            attribute_id:   attrId,
                            attribute_name: colName
                        },
                        transform: colExpr ? { expression: colExpr } : null
                    });
                });
            }
        });

        return result;
    }

    // ========================================
    // CANCEL
    // ========================================

    ctrl.cancel = function () {
        if (!ctrl.hasChanges) { $mdDialog.cancel(); return; }
        dialogService.showDecisionDialog(
            'Discard Changes',
            'You have unsaved changes. Are you sure you want to close without applying them?'
        ).then(function (decision) {
            if (decision === 'proceed') { $mdDialog.cancel(); }
        });
    };

    // ========================================
    // INTELLISENSE (same flow as create wizard)
    // ========================================

    // Normalizes a wizard operation object into the shape the IntelliSense
    // API expects. Shared by the discovery pass and by the re-scoped
    // recompute below, so both build the request payload identically.
    function _toApiOperation(op, cardinality) {
        return {
            operation_id:   op.operationId   || op.operation_id   || op.id,
            operation_name: op.operationName || op.operation_name || op.summary || op.path,
            method:         op.method,
            path:           op.path,
            summary:        op.summary || null,
            payload:        op.payload || null,
            cardinality:    cardinality || 'ONE'
        };
    }

    // Runs one IntelliSense mapping job end-to-end: starts it, shows a
    // progress dialog, chain-polls until it settles, and resolves with
    // { mappings, summary }. Rejects with { cancelled: true } if the user
    // cancels, or { title, message } on any other failure.
    //
    // opts (optional):
    //   engine       — BusinessEventsService.INTELLISENSE_ENGINE.* (default
    //                  DETERMINISTIC); also sets the polling interval.
    //   dialogTitle  — overrides the progress dialog's toolbar title.
    //   initialStage — overrides the stage text shown before the first
    //                  SSE progress event arrives.
    //
    // Used by ctrl.runIntelliSenseSelection with the deterministic engine
    // against every scoped operation to let the user pick which ones to keep,
    // and with the agent when leaving Configure Operations (_runAgentMapping,
    // _remapWithChanges) to build the mappings handed to the mapping step.
    //
    // Uses $q, not native Promise: everything chained onto this — including
    // the ctrl.selectedOperations / ctrl._intelliSenseMappings assignments
    // in runIntelliSenseSelection's .then() handlers below — needs to run
    // inside Angular's digest cycle so the view actually re-renders when it
    // resolves. A native Promise would still resolve correctly, but its
    // .then() callbacks fire as plain JS microtasks outside any $digest, so
    // the state change wouldn't reach the DOM until some unrelated event
    // happened to trigger the next digest.
    function _runIntelliSenseJob(operations, opts) {
        opts = opts || {};
        var engine = opts.engine || BusinessEventsService.INTELLISENSE_ENGINE.DETERMINISTIC;
        return $q(function (resolve, reject) {
            var _cancelled   = false;
            var _jobId       = null;   // known once /start answers — needed to cancel the agent
            var _pollTimeout = null;   // $timeout handle for chained polling

            function _stopPoll() {
                if (_pollTimeout) { $timeout.cancel(_pollTimeout); _pollTimeout = null; }
            }

            // ── Show in-progress dialog ────────────────────────────────────
            // Always shown — even on the pass-2 re-scope recompute, since that
            // call still hits the backend and can take a few seconds.
            $mdDialog.show({
                multiple: true,
                clickOutsideToClose: false,
                escapeToClose: false,
                template: _IS_PROGRESS_DIALOG_TEMPLATE,
                controller: function ($scope, $mdDialog) {
                    $scope.percent     = 0;
                    $scope.stage       = opts.initialStage || 'Starting…';
                    $scope.dialogTitle = opts.dialogTitle   || null;   // falls back to default in the template

                    $scope.$on('intellisense:progress', function (e, data) {
                        $scope.percent = data.percent || 0;
                        $scope.stage   = data.message || '';
                    });

                    $scope.cancelIntelliSense = function () {
                        _cancelled = true;
                        _stopPoll();
                        BusinessEventsService.cancelIntelliSense(_jobId, engine);   // stop the agent server-side
                        $mdDialog.hide('cancelled');
                        reject({ cancelled: true });
                    };
                }
            });

            // Chained $timeout polling — only ONE request in flight at a time.
            function _schedulePoll(jobId) {
                if (_cancelled) { return; }
                _pollTimeout = $timeout(function () {
                    BusinessEventsService.pollIntelliSenseStatus(jobId, engine)
                        .then(function (res) {
                            if (_cancelled) { return; }
                            var data = res.data;

                            $rootScope.$broadcast('intellisense:progress', {
                                percent: data.percent || 0,
                                message: data.message  || ''
                            });

                            if (data.status === 'COMPLETE') {
                                _stopPoll();
                                $rootScope.$broadcast('intellisense:progress', { percent: 100, message: 'Done' });

                                $timeout(function () {
                                    $mdDialog.hide();
                                    resolve({
                                        mappings: (data.result && data.result.mappings) || [],
                                        summary:  (data.result && data.result.summary)  || {}
                                    });
                                }, 400);  // let the 100% bar render before closing

                            } else if (data.status === 'ERROR') {
                                _stopPoll();
                                $mdDialog.hide();
                                reject({
                                    title:   'IntelliSense Failed',
                                    message: data.message || 'IntelliSense processing failed.'
                                });

                            } else {
                                _schedulePoll(jobId);
                            }
                        })
                        .catch(function (err) {
                            if (_cancelled) { return; }
                            _stopPoll();
                            $mdDialog.hide();
                            reject({
                                title:   'IntelliSense Connection Error',
                                message: (err.data && (err.data.detail || err.data.message)) ||
                                    'Lost connection to IntelliSense service.'
                            });
                        });
                }, BusinessEventsService.intelliSensePollInterval(engine));
            }

            BusinessEventsService.startIntelliSense({
                operations:    operations,
                template:      ctrl.selectedTemplateFull,
                frame_type_id: ctrl.selectedFrameType.frame_type_id
            }, engine)
            .then(function (response) {
                _jobId = response.data && response.data.job_id;
                if (_cancelled) { BusinessEventsService.cancelIntelliSense(_jobId, engine); return; }
                var jobId = response.data && response.data.job_id;
                if (!jobId) { throw new Error('No job_id returned'); }
                _schedulePoll(jobId);
            })
            .catch(function (err) {
                if (_cancelled) { return; }
                $mdDialog.hide();
                _stopPoll();
                reject({
                    title:   'IntelliSense Failed',
                    message: (err.data && (err.data.detail || err.data.message)) || 'Failed to start IntelliSense.'
                });
            });
        });
    }

    // The composer only loads its preload into an empty mapping list. Unlike the
    // create wizard, a version being edited already has mappings, so the
    // IntelliSense result is merged into them and handed over as the new preload
    // (sources are matched by operation id, so the rebuilt selection order doesn't
    // matter): components without a mapping take IntelliSense's, and a nested
    // table that keeps its mapping gains the columns IntelliSense mapped from the
    // same array. With `replace`, IntelliSense's mapping wins for every component
    // it mapped — including its BMEL: the component's current expressions are
    // dropped, since the composer never overwrites an existing one.
    function _handIntelliSenseToComposer(isMappings, baseMappings, replace) {
        ctrl._intelliSenseMappings = isMappings;
        var merged = angular.copy(baseMappings || []);
        var byComponent = {};
        merged.forEach(function (c) { byComponent[c.frame_component_id] = c; });

        (isMappings || []).forEach(function (suggested) {
            var existing = byComponent[suggested.frame_component_id];
            if (existing && replace && (suggested.sources || []).length) {
                var compId = suggested.frame_component_id;
                merged[merged.indexOf(existing)] = byComponent[compId] = angular.copy(suggested);
                _forgetExpressions([compId]);
                return;
            }
            if (!existing) {
                var copy = angular.copy(suggested);
                merged.push(copy);
                byComponent[copy.frame_component_id] = copy;
                return;
            }
            (suggested.sources || []).forEach(function (ss) {
                if (!ss.column_mappings || !ss.column_mappings.length) { return; }
                var es = (existing.sources || []).find(function (x) {
                    return String(x.operation_id) === String(ss.operation_id) && x.field_path === ss.field_path;
                });
                if (!es) { return; }
                es.column_mappings = es.column_mappings || [];
                var have = {};
                es.column_mappings.forEach(function (cm) { have[cm.column_name] = true; });
                ss.column_mappings.forEach(function (cm) {
                    if (!have[cm.column_name]) { es.column_mappings.push(angular.copy(cm)); }
                });
            });
        });

        ctrl._preloadMappings = merged;
        ctrl.mappings = [];   // the composer re-applies the merged preload when it opens
    }

    ctrl.runIntelliSenseSelection = function () {
        if (ctrl.intelliSenseSelecting) { return; }
        if (!ctrl.selectedTemplateFull || !ctrl.selectedFrameType) { return; }
        if (ctrl.scopedCollections.length === 0) {
            ctrl.intelliSenseSelectionError = 'Browse at least one API collection first.';
            return;
        }

        ctrl.intelliSenseSelecting      = true;
        ctrl.intelliSenseSelectionError = null;

        // Populated by pass 1, read back by the results dialog below
        var discoveredOps = [];

        // ── Pass 1 (discover, deterministic engine): run against EVERY scoped
        // operation so the results dialog can show the user which operations
        // best match the frame type, regardless of which ones they keep.
        // Its mappings are only used to pick operations — the agent builds
        // the real ones in pass 2.
        var allOps = [];
        ctrl.scopedCollections.forEach(function (sc) {
            (sc.operations || []).forEach(function (op) {
                allOps.push(_toApiOperation(op));
            });
        });

        _runIntelliSenseJob(allOps, { engine: BusinessEventsService.INTELLISENSE_ENGINE.DETERMINISTIC })
            .then(function (discovery) {
                var mappings = discovery.mappings;

                var matchedIds = {};
                mappings.forEach(function (m) {
                    (m.sources || []).forEach(function (src) {
                        if (src.operation_id) { matchedIds[src.operation_id] = true; }
                    });
                });

                discoveredOps = [];
                ctrl.scopedCollections.forEach(function (sc) {
                    (sc.operations || []).forEach(function (op) {
                        var opId = op.operationId || op.operation_id || op.id;
                        if (matchedIds[String(opId)]) {
                            discoveredOps.push({ op: op, collection: sc.collection });
                        }
                    });
                });

                return $mdDialog.show({
                    controller:          'IntelliSenseResultsController',
                    controllerAs:        'ctrl',
                    template:            _IS_RESULTS_DIALOG_TEMPLATE,
                    parent:              angular.element(document.body),
                    clickOutsideToClose: false,
                    escapeToClose:       true,
                    multiple:            true,
                    locals: {
                        discoveredOps: discoveredOps,
                        mappings:      mappings,
                        summary:       discovery.summary,
                        frameTypeName: ctrl.selectedFrameType ? ctrl.frameTypeName(ctrl.selectedFrameType) : '',
                        templateVersion: ctrl.templateVersion()
                    }
                });
            })
            .then(function (result) {
                // result is either { action: 'proceed', selectedOps } or the
                // plain string 'cancel' (from the results dialog's Cancel/X).
                if (!result || result.action !== 'proceed') {
                    ctrl.intelliSenseSelecting = false;
                    return;
                }

                // Mappings made so far — the saved ones, or edits already made in the
                // composer — captured by operation (not by index) before the
                // selection is rebuilt, so IntelliSense only fills what's missing.
                var baseMappings = (ctrl.mappings && ctrl.mappings.length)
                    ? _convertApiMappingsToIntelliSenseFormat(_buildMappingsPayload())
                    : (ctrl._preloadMappings || []);

                ctrl.selectedOperations = [];
                result.selectedOps.forEach(function (d) {
                    ctrl._addOperation(d.op, d.collection);
                });
                ctrl._recomputeDirty();

                // The selection was rebuilt, so op indices changed: hand the
                // composer the mappings made so far (captured by operation
                // above). The agent fills the gaps once, when leaving Configure
                // Operations — after the user sets each operation's cardinality.
                ctrl._intelliSenseMappings      = null;
                ctrl._preloadMappings           = baseMappings;
                ctrl.mappings                   = [];
                ctrl._agentPending              = result.selectedOps.length > 0;
                ctrl.intelliSenseSelectionError = null;
                ctrl.intelliSenseSelecting      = false;
            })
            .catch(function (err) {
                ctrl.intelliSenseSelecting = false;
                if (!err || err.cancelled) { return; }   // user cancelled — nothing to report
                ctrl._intelliSenseMappings = null;        // don't keep stale/mismatched mappings on failure
                dialogService.showErrorDialog(
                    err.title   || 'IntelliSense Failed',
                    err.message || 'IntelliSense processing failed. Please try again or map the fields manually.'
                );
            });
    };

    // ── IntelliSense dialog templates (shared with create wizard) ─────────────

    var _IS_PROGRESS_DIALOG_TEMPLATE = [
        '<md-dialog class="cbew-is-progress-dialog" aria-label="IntelliSense in progress">',
        '  <md-toolbar style="background:linear-gradient(135deg,#667eea 0%,#764ba2 100%);min-height:64px;flex-shrink:0;">',
        '    <div class="md-toolbar-tools">',
        '      <md-icon style="color:white;margin-right:10px;flex-shrink:0;">auto_awesome</md-icon>',
        '      <h2 style="color:white;font-weight:500;font-size:18px;margin:0;flex:1;text-align:left;">{{ dialogTitle || "IntelliSense in Progress..." }}</h2>',
        '    </div>',
        '  </md-toolbar>',
        '  <md-dialog-content style="padding:24px 28px 20px;background:#fff;min-width:420px;">',
        '    <div style="display:flex;align-items:center;gap:12px;margin-bottom:16px;">',
        '      <md-progress-circular md-mode="indeterminate" md-diameter="22" style="flex-shrink:0;"></md-progress-circular>',
        '      <span style="font-size:13px;color:#546e7a;flex:1;">{{ stage || "Starting..." }}</span>',
        '      <span style="font-size:13px;font-weight:700;color:#764ba2;min-width:36px;text-align:right;">{{ percent }}%</span>',
        '    </div>',
        '    <div style="height:8px;background:#ede7f6;border-radius:4px;overflow:hidden;">',
        '      <div style="height:100%;border-radius:4px;background:linear-gradient(90deg,#667eea 0%,#764ba2 100%);transition:width 0.4s ease;"',
        '           ng-style="{width:(percent||0)+\'%\'}"></div>',
        '    </div>',
        '    <p style="margin:10px 0 0 0;font-size:12px;color:#b0bec5;text-align:right;">You can cancel at any time.</p>',
        '  </md-dialog-content>',
        '  <div style="display:flex;justify-content:flex-end;padding:10px 16px;background:#fafafa;border-top:1px solid #eee;">',
        '    <button type="button" ng-click="cancelIntelliSense()"',
        '            style="background:#f5f5f5;color:#666;border:1px solid #ddd;border-radius:4px;font-size:13px;font-weight:500;height:34px;padding:0 20px;cursor:pointer;font-family:inherit;">',
        '      Cancel',
        '    </button>',
        '  </div>',
        '</md-dialog>'
    ].join('\n');

    var _IS_RESULTS_DIALOG_TEMPLATE = [
        '<md-dialog class="cbew-is-results-dialog" aria-label="IntelliSense Results" style="max-width:92vw;">',
        '  <md-toolbar style="background:linear-gradient(135deg,#667eea 0%,#764ba2 100%);min-height:64px;flex-shrink:0;">',
        '    <div class="md-toolbar-tools">',
        '      <md-icon style="color:white;margin-right:10px;">auto_awesome</md-icon>',
        '      <div style="flex:1;display:flex;flex-direction:column;gap:1px;text-align:left;">',
        '        <h2 style="color:white;font-weight:500;font-size:18px;margin:0;text-align:left;">IntelliSense Results</h2>',
        '        <span style="color:rgba(255,255,255,0.8);font-size:12px;">',
        '          {{ ctrl.discoveredOps.length > 0 ? (ctrl.selectedCount() + \' of \' + ctrl.discoveredOps.length + \' operation\' + (ctrl.discoveredOps.length===1?\'\':\'s\')+\' selected\') : \'No matches found\' }}',
        '        </span>',
        '      </div>',
        '      <md-button class="md-icon-button" ng-click="ctrl.dismiss()" style="color:white;margin:0;"><md-icon>close</md-icon></md-button>',
        '    </div>',
        '  </md-toolbar>',
        '  <md-dialog-content style="padding:0;background:#fff;width:860px;max-width:92vw;">',
        '    <div ng-if="ctrl.discoveredOps.length === 0" style="padding:36px 28px;display:flex;flex-direction:column;align-items:center;gap:16px;text-align:center;">',
        '      <div style="width:56px;height:56px;border-radius:50%;background:#f3f0ff;display:flex;align-items:center;justify-content:center;">',
        '        <md-icon style="font-size:28px;width:28px;height:28px;color:#9575cd;">search_off</md-icon>',
        '      </div>',
        '      <div><p style="margin:0 0 8px 0;font-size:14px;font-weight:600;color:#37474f;">No matches found</p></div>',
        '    </div>',
        '    <div ng-if="ctrl.discoveredOps.length > 0">',
        '      <div style="padding:14px 24px;background:#f3f0ff;border-bottom:1px solid #e0e0e0;display:flex;align-items:center;gap:12px;">',
        '        <md-icon style="color:#764ba2;font-size:20px;width:20px;height:20px;flex-shrink:0;">info_outline</md-icon>',
        '        <span style="font-size:13px;color:#4a3f6b;line-height:1.4;flex:1;">Matched operations for frame type <strong>{{ ctrl.frameTypeName }}</strong> <span class="cbew-tpl-version-pill cbew-op-version-pill" ng-if="ctrl.templateVersion">v{{ ctrl.templateVersion }}</span>. Select and click Proceed.</span>',
        '        <label style="display:flex;align-items:center;gap:6px;cursor:pointer;flex-shrink:0;font-size:12px;color:#4a3f6b;user-select:none;white-space:nowrap;">',
        '          <input type="checkbox" ng-checked="ctrl.allSelected()" ng-click="ctrl.toggleAll()" style="width:15px;height:15px;accent-color:#764ba2;cursor:pointer;">',
        '          Select all',
        '        </label>',
        '      </div>',
        '      <div style="max-height:60vh;overflow-y:auto;">',
        '        <div ng-repeat="item in ctrl.discoveredOps track by $index"',
        '             style="display:flex;align-items:center;gap:14px;padding:12px 24px;border-bottom:1px solid #f0f0f0;cursor:pointer;"',
        '             ng-class="{\'cbew-is-result-row--selected\':ctrl.selected[$index]}" ng-click="ctrl.selected[$index]=!ctrl.selected[$index]">',
        '          <input type="checkbox" ng-model="ctrl.selected[$index]" ng-click="$event.stopPropagation()" style="width:15px;height:15px;accent-color:#764ba2;cursor:pointer;flex-shrink:0;">',
        '          <span class="cbew-method-badge cbew-method-badge--{{ item.op.method | lowercase }}">{{ item.op.method }}</span>',
        '          <div style="flex:1;min-width:0;">',
        '            <div style="font-size:13px;font-weight:500;color:#37474f;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">{{ item.op.operationName||item.op.summary||item.op.path }} <span class="cbew-tpl-version-pill cbew-op-version-pill" ng-if="item.collection.api_version">v{{ item.collection.api_version }}</span></div>',
        '            <div style="font-size:11px;color:#90a4ae;font-family:monospace;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;" title="{{ item.op.path }}">{{ item.op.path }}</div>',
        '          </div>',
        '          <span style="font-size:11px;color:#9575cd;background:#f3f0ff;padding:3px 10px;border-radius:10px;white-space:nowrap;flex-shrink:0;max-width:45%;overflow:hidden;text-overflow:ellipsis;" title="{{ item.collection.collection_name }} v{{ item.collection.api_version }}">{{ item.collection.collection_name }}</span>',
        '        </div>',
        '      </div>',
        '    </div>',
        '  </md-dialog-content>',
        '  <md-dialog-actions style="padding:12px 16px;background:#fafafa;border-top:1px solid #e0e0e0;display:flex;justify-content:flex-end;gap:8px;min-height:52px;">',
        '    <md-button ng-if="ctrl.discoveredOps.length > 0" class="cbew-btn cbew-btn--cancel" ng-click="ctrl.cancel()">Cancel</md-button>',
        '    <md-button class="md-raised cbew-is-proceed-btn" ng-click="ctrl.proceed()" ng-disabled="ctrl.discoveredOps.length > 0 && ctrl.selectedCount() === 0">',
        '      <md-icon style="font-size:18px;width:18px;height:18px;margin-right:4px;">check_circle</md-icon>',
        '      Proceed{{ ctrl.discoveredOps.length > 0 && ctrl.selectedCount() > 0 ? \' (\' + ctrl.selectedCount() + \')\' : \'\' }}',
        '    </md-button>',
        '  </md-dialog-actions>',
        '</md-dialog>'
    ].join('\n');

    // ========================================
    // INITIALIZATION — fetch full version data and pre-populate
    // ========================================


    // ── Converts biz_event_mappings (API format) → IntelliSense format ──────────
    // The directive's _translateIntelliSenseMappings expects the IntelliSense shape.
    // We group API mapping entries by component_id, collecting their sources.
    //
    // API entry:
    //   sources[]: { operation_name, source_type, source_path, placeholder }
    //   target:    { component_id, component_name, component_type, attribute_name }
    //   transform: { expression }
    //
    // IntelliSense entry (what directive expects):
    //   frame_component_id, frame_field_name,
    //   sources[]: { operation_id, source_type, field_path, field_name, column_mappings[] }
    //   bmel_expression
    // ─────────────────────────────────────────────────────────────────────────────
    function _convertApiMappingsToIntelliSenseFormat(apiMappings) {
        if (!apiMappings || !apiMappings.length) { return []; }

        // Build a lookup: operation_name → opIdx in selectedOperations
        // (matching by operationName, summary, or path)
        var opNameToIdx = {};
        ctrl.selectedOperations.forEach(function (entry, idx) {
            var op = entry.op;
            var names = [
                op.operationName,
                op.operationId,
                op.summary,
                op.path
            ].filter(Boolean);
            names.forEach(function (n) {
                if (n && !opNameToIdx.hasOwnProperty(n)) {
                    opNameToIdx[n] = idx;
                }
            });
        });

        // Group by component_id — FIELD_REFERENCE entries (non-NESTED_TABLE) build
        // one IntelliSense entry per component. NESTED_TABLE column entries get merged
        // into column_mappings[] of the parent component entry.
        var componentMap = {};   // component_id → IS entry being built
        var componentOrder = []; // preserve insertion order

        apiMappings.forEach(function (entry) {
            var tgt       = entry.target  || {};
            var transform = entry.transform || {};
            var compId    = tgt.component_id;
            var compName  = tgt.component_name;
            var compType  = tgt.component_type || 'FIELD_REFERENCE';
            var attrName  = tgt.attribute_name;   // for NESTED_TABLE columns
            var bmelExpr  = transform.expression  || null;

            if (!compId) { return; }

            // Resolve operation index for each source
            (entry.sources || []).forEach(function (src) {
                var opName = src.operation_name || '';
                var opIdx  = opNameToIdx.hasOwnProperty(opName) ? opNameToIdx[opName] : -1;
                var opEntry = opIdx >= 0 ? ctrl.selectedOperations[opIdx] : null;
                var operationId = opEntry ? (opEntry.op.operationId || opEntry.op.operationName || opName) : opName;

                // ── CONTEXT source: resolve the DOM-matching field_path ──────────────
                // The directive stores context var mappings with source.id = DOM data-field-id.
                // Those IDs are built as 'op{idx}_{CV_BASE_ID}' for $bm.* vars and
                // 'op{idx}_CORR_ID' for the correlation key field.
                //
                // _translateIntelliSenseMappings builds sourceId as:
                //   'op' + opIdx + '_' + frontendType + '_' + field_path
                //
                // So for CONTEXT we need field_path to equal the CV base_id or 'CORR_ID',
                // and frontendType to be empty — achieved by passing source_type='' and
                // putting the full CV id in field_path so the result is 'op0_CV_REQ_SIZE'.
                //
                // Approach: for CONTEXT sources, override field_path to the base_id directly
                // so the assembled sourceId matches the DOM node exactly.


                if (compType === 'NESTED_TABLE' && attrName) {
                    // ── Column entry — merge into parent component ──────────────
                    if (!componentMap[compId]) {
                        // Create a skeleton parent entry (may be filled in when the
                        // main FIELD_REFERENCE entry appears, or stay as skeleton if
                        // the backend only sent NESTED_TABLE rows for this component)
                        componentMap[compId] = {
                            frame_component_id: compId,
                            frame_field_name:   compName,
                            sources:            [],
                            bmel_expression:    null
                        };
                        componentOrder.push(compId);
                    }
                    var parentEntry = componentMap[compId];

                    // Find or create the source entry in the parent (one source per op)
                    var parentSrc = parentEntry.sources.find(function (s) {
                        return String(s.operation_id) === String(operationId);
                    });
                    if (!parentSrc) {
                        parentSrc = {
                            operation_id:    operationId,
                            source_type:     src.source_type,
                            field_path:      src.source_path || '',
                            field_name:      null,
                            column_mappings: []
                        };
                        parentEntry.sources.push(parentSrc);
                    }
                    if (!parentSrc.column_mappings) { parentSrc.column_mappings = []; }
                    parentSrc.column_mappings.push({
                        column_name:  attrName,
                        // Columns of a MANY-operation NESTED_TABLE can come from any
                        // section of that operation (not just the row array's own
                        // section), so the column keeps its own source_type.
                        source_type:  src.source_type,
                        field_path:   src.source_path || '',
                        field_name:   null,
                        bmel_expression: bmelExpr
                    });

                } else {
                    // ── Main component entry ────────────────────────────────────
                    if (!componentMap[compId]) {
                        componentMap[compId] = {
                            frame_component_id: compId,
                            frame_field_name:   compName,
                            sources:            [],
                            bmel_expression:    null
                        };
                        componentOrder.push(compId);
                    }
                    var isEntry = componentMap[compId];
                    isEntry.bmel_expression = isEntry.bmel_expression || bmelExpr;

                    isEntry.sources.push({
                        operation_id:    operationId,
                        source_type:     src.source_type,
                        field_path:      src.source_path || '',
                        field_name:      null,
                        column_mappings: []
                    });
                }
            });
        });

        return componentOrder.map(function (compId) { return componentMap[compId]; });
    }

    function _preloadVersionData(fullVersion) {
        // ── Pre-populate operations ────────────────────────────────────────────
        // We need to load collections first, then match operations
        var sources = fullVersion.biz_event_sources || [];
        var mappings = fullVersion.biz_event_mappings || [];
        ctrl._originalApiMappings = mappings;

        if (sources.length === 0) {
            ctrl.loadingVersion = false;
            ctrl._initialLoadDone = true;
            return;
        }

        // Collect unique collection IDs referenced by the sources
        var colIds = [];
        sources.forEach(function (src) {
            if (src.api_collection_id && colIds.indexOf(src.api_collection_id) === -1) {
                colIds.push(src.api_collection_id);
            }
        });

        // Fetch only the collections the sources use (operations / swagger) —
        // the picker lists the template's own collections separately.
        $q.all(colIds.map(function (id) {
            return ApiCollectionService.getById(id)
                .then(function (r) { return r.data || r; })
                .catch(function () { return null; });   // deleted since — its sources are skipped
        }))
            .then(function (loadedCols) {
                loadedCols = loadedCols.filter(Boolean);
                // Build a map colId → loaded collection
                var colMap = {};
                loadedCols.forEach(function (col) { colMap[col.collection_id] = col; });

                // For each source, find or build the matching op and add it
                var addedKeys = {};  // prevent duplicates

                sources.forEach(function (src) {
                    var col = colMap[src.api_collection_id];
                    if (!col) { return; }

                    var ops = col.operations
                        ? ctrl._opsFromMetadata(col.operations)
                        : ctrl._parseOperations(col.swagger);

                    // Match by operationId or (method+path)
                    var matchedOp = ops.find(function (op) {
                        if (src.operation_id && op.operationId) {
                            return String(op.operationId) === String(src.operation_id);
                        }
                        return op.method === (src.api_method || '').toUpperCase() &&
                               op.path   === src.api_endpoint;
                    });

                    if (!matchedOp) { return; }

                    var opKey = src.api_collection_id + '|' + matchedOp._pathKey + '|' + matchedOp._method;
                    if (addedKeys[opKey]) { return; }
                    addedKeys[opKey] = true;

                    // Add to scoped collections if not already there
                    if (!ctrl.isScopedCollection(col)) {
                        ctrl.scopedCollections.push({ collection: col, operations: ops });
                    }

                    // Build optionsByType for the op
                    var optionsByType = {};
                    ctrl.correlationSourceTypes.forEach(function (cst) {
                        var opts = _computeCorrelationOptions(matchedOp, cst.id);
                        opts.sort(function (a, b) { return a.name < b.name ? -1 : a.name > b.name ? 1 : 0; });
                        optionsByType[cst.id] = opts;
                    });

                    ctrl.selectedOperations.push({
                        op:               matchedOp,
                        collection:       col,
                        interceptionMode: src.event_interception_mode || 'INBOUND',
                        cardinality:      src.cardinality || 'ONE',
                        correlationKey: {
                            sourceType: src.correlation_key ? src.correlation_key.source_type : null,
                            sourcePath: src.correlation_key ? src.correlation_key.source_path  : ''
                        },
                        filters: (src.filters || []).map(function (f) {
                            return {
                                filterType:    f.filter_type,
                                sourcePath:    f.source_path,
                                allowedValues: f.allowed_values || [],
                                caseSensitive: f.case_sensitive !== false,
                                _newValue:     '',
                                _open:         false
                            };
                        }),
                        _optionsByType: optionsByType,
                        _configOpen:   false
                    });
                });

                // ── Pre-populate mappings ─────────────────────────────────────
                // _translateIntelliSenseMappings (inside the directive) expects the
                // IntelliSense API shape:
                //   { frame_component_id, frame_field_name,
                //     sources: [{ operation_id, source_type, field_path, field_name, column_mappings }],
                //     bmel_expression }
                //
                // But biz_event_mappings from the backend use a different shape:
                //   { sources: [{ operation_name, source_type, source_path }],
                //     target:  { component_id, component_name, component_type, attribute_name },
                //     transform: { expression } }
                //
                // We convert here so the directive can reuse the same translation path.
                ctrl._preloadMappings = _convertApiMappingsToIntelliSenseFormat(mappings);
                ctrl._mappedInputs    = _currentAgentInputs(ctrl._originalTarget && ctrl._originalTarget.frame_type_id, undefined);

            })
            .catch(function (err) {
                console.error('[EditBizEventVersion] Error pre-loading operations:', err);
            })
            .finally(function () {
                ctrl.loadingVersion   = false;
                ctrl._initialLoadDone = true;
            });
    }

    // ── IntelliSense still at work after a recompute ──────────────────────────
    // A template / API change left targets unmapped and IntelliSense (~1 min) is mapping them.
    // The wizard loads its state once, so when it finishes it offers to reopen with them.
    ctrl.intelliSensePendingSince = null;
    ctrl.intelliSenseUpdateReady  = false;
    var _stopIntelliSenseWatch = angular.noop;

    function _watchIntelliSense(fullVersion) {
        ctrl.intelliSensePendingSince = BusinessEventsService.intelliSensePendingSince(fullVersion);
        if (!ctrl.intelliSensePendingSince) { return; }
        _stopIntelliSenseWatch = BusinessEventsService.watchIntelliSenseRecompute(
            bizEvent.biz_event_id, version.biz_event_version_id, function (fresh) {
                ctrl.intelliSensePendingSince = null;
                ctrl.intelliSenseUpdateReady  = true;
                ctrl._intelliSenseVersion     = fresh;
            });
    }
    $scope.$on('$destroy', function () { _stopIntelliSenseWatch(); });

    ctrl.intelliSensePendingAgo = function () {
        if (!ctrl.intelliSensePendingSince) { return ''; }
        var secs = Math.max(0, Math.round((Date.now() - ctrl.intelliSensePendingSince) / 1000));
        return secs < 60 ? secs + 's ago' : Math.floor(secs / 60) + ' min ago';
    };

    /** Reopen the editor on the version as IntelliSense left it (the tab reopens it). */
    ctrl.reopenWithIntelliSenseMappings = function () {
        var reopen = { reopen: true, version: ctrl._intelliSenseVersion || null };
        if (!ctrl.hasChanges) { $mdDialog.hide(reopen); return; }
        dialogService.showDecisionDialog(
            'Load IntelliSense Mappings',
            'Reopening the editor shows the mappings IntelliSense just added and discards your unsaved changes. Continue?'
        ).then(function (decision) {
            if (decision === 'proceed') { $mdDialog.hide(reopen); }
        });
    };

    // Kick off initialization: fetch the full version detail via getById
    // (the list response may not include full biz_event_mappings)
    BusinessEventsService.getById(bizEvent.biz_event_id)
        .then(function (response) {
            var fullEvent = response.data;
            if (!fullEvent) { throw new Error('Empty response'); }

            // Find the matching version
            var fullVersion = null;
            if (fullEvent.versions && fullEvent.versions.length) {
                fullVersion = fullEvent.versions.find(function (v) {
                    return v.biz_event_version_id === version.biz_event_version_id;
                });
            }
            // Fallback: the response might be the version directly
            if (!fullVersion) { fullVersion = version; }
            _watchIntelliSense(fullVersion);

            // Load the target frame (templates)
            ctrl.loadPublishedTemplates();

            // Pre-populate operations and mappings
            _preloadVersionData(fullVersion);
        })
        .catch(function (err) {
            console.error('[EditBizEventVersion] Error fetching biz event detail:', err);
            ctrl.loadingVersion   = false;
            ctrl._initialLoadDone = true;
        });

});